import React, { useState, useEffect } from 'react';
import { User, Lock, Eye, EyeOff, CheckCircle, Clock, AlertTriangle, Upload, FileText, PenTool, Shield, ArrowLeft, Save, Send, Phone, Mail, MapPin, Calendar, Users, FileCheck, UserCheck, Settings, Plus, X, DollarSign, Landmark, ChevronDown, ChevronRight, Download, Trash2, Search } from 'lucide-react';

// W-4 Form Reference Documents (served from backend)
const FEDERAL_W4_PDF_URL = '/documents/2026_Fed_W4.pdf';
const SC_W4_PDF_URL = '/documents/SCW4_2026.pdf';

// CPW Brand Assets
const CPW_LOGO_URL = '/documents/CPW_Logo.png';

// CPW Brand Color Overrides (injected as global CSS)
// Maps Tailwind's default blue utilities to Greenwood CPW style guide colors
const CPW_BRAND_STYLES = `
  /* === Greenwood CPW Brand Colors === */
  /* Primary: 285 C (#0072CE) | Navy: 2748 C (#001871) | Light: 278 C (#8BB8E8) */
  /* Gold: 7408 C (#F6BE00) | Black: Black C (#2D2926) | Warm Gray: (#B6ADA5) */

  /* Typography — Primary font: Arial per CPW Style Guide */
  body, input, select, textarea, button { font-family: Arial, Calibri, Cambria, sans-serif !important; }

  /* Primary blue overrides → CPW Blue 285 C */
  .bg-blue-600 { background-color: #0072CE !important; }
  .bg-blue-700 { background-color: #001871 !important; }
  .bg-blue-50 { background-color: #E8F1FA !important; }
  .bg-blue-100 { background-color: #D1E4F6 !important; }

  .text-blue-600 { color: #0072CE !important; }
  .text-blue-700 { color: #005BA6 !important; }
  .text-blue-800 { color: #001871 !important; }
  .text-blue-100 { color: #D1E4F6 !important; }

  .border-blue-200 { border-color: #8BB8E8 !important; }
  .border-blue-300 { border-color: #6BA3DE !important; }

  /* Hover state overrides */
  .hover\\:bg-blue-700:hover { background-color: #001871 !important; }
  .hover\\:bg-blue-50:hover { background-color: #E8F1FA !important; }
  .hover\\:text-blue-700:hover { color: #001871 !important; }
  .hover\\:text-blue-800:hover { color: #001871 !important; }

  /* Focus ring overrides */
  .focus\\:ring-blue-500:focus { --tw-ring-color: #0072CE !important; }

  /* Checkbox/radio accent overrides */
  input[type="checkbox"].text-blue-600,
  input[type="radio"].text-blue-600 { accent-color: #0072CE !important; }

  /* Indigo overrides (used in some gradients) → map to CPW Navy */
  .bg-indigo-600 { background-color: #001871 !important; }
  .from-indigo-50 { --tw-gradient-from: #E8F1FA !important; }
  .to-indigo-50 { --tw-gradient-to: #E8F1FA !important; }
`;


// Type definitions
type TaskStatus = 'completed' | 'in-progress' | 'pending';

interface TaskData {
  status: TaskStatus;
  data: Record<string, any>;
}

interface EmployeeTasks {
  personalInfo: TaskData;
  identification: TaskData;
  taxForms: TaskData;
  directDeposit: TaskData;
  benefits: TaskData;
  policies: TaskData;
}

interface Employee {
  id: number;
  name: string;
  position: string;
  department: string;
  email: string;
  startDate: string;
  progress: number;
  tasks: EmployeeTasks;
}

interface UploadedFile {
  id: number;
  name: string;
  size: number;
  type: string;
  uploadDate: string;
  actualFile?: File;
}

interface IdentificationFiles {
  driversLicense: UploadedFile | null;
  socialSecurity: UploadedFile | null;
  other: UploadedFile | null;
}

interface PersonalInfo {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  emergencyContact: string;
  emergencyPhone: string;
}

interface FederalW4Data {
  firstName: string;
  middleInitial: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  ssn: string;
  filingStatus: 'single_separate' | 'married_jointly' | 'head_of_household' | '';
  multipleJobsCheckbox: boolean;
  qualifyingChildren: string;
  otherDependents: string;
  otherCredits: string;
  otherIncome: string;
  deductions: string;
  extraWithholding: string;
  claimExempt: boolean;
  signature: string;
  signatureDate: string;
}

interface SCW4Data {
  firstName: string;
  middleInitial: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  ssn: string;
  filingStatus: 'single' | 'married' | 'married_higher_single' | '';
  lastNameDiffers: boolean;
  totalAllowances: string;
  additionalWithholding: string;
  claimExempt: boolean;
  exemptReason: 'no_liability' | 'military' | '';
  militaryDomicileState: string;
  signature: string;
  signatureDate: string;
}

interface BenefitsData {
  benefitGuideAcknowledged: boolean;
  scrsHandbookAcknowledged: boolean;
}

interface HandbookData {
  handbookAcknowledged: boolean;
}

interface DirectDepositData {
  depositoryName: string;
  depositoryCity: string;
  depositoryState: string;
  transitABANumber: string;
  accountNumber: string;
  accountType: 'checking' | 'savings' | '';
  depositType: 'full' | 'specific' | '';
  specificAmount: string;
  employeeName: string;
  payrollId: string;
  authorizationAcknowledged: boolean;
  signature: string;
  signatureDate: string;
}

interface DirectDepositFiles {
  voidedCheck: UploadedFile | null;
}

interface NewEmployeeForm {
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  department: string;
  startDate: string;
  temporaryPassword: string;
}

interface OnboardingTask {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  icon: any;
  progress: number;
}

// Utility functions (extracted outside component)
const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case 'completed': return 'text-green-600 bg-green-50 border-green-200';
    case 'in-progress': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'pending': return 'text-gray-600 bg-gray-50 border-gray-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const getStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case 'completed': return CheckCircle;
    case 'in-progress': return Clock;
    case 'pending': return AlertTriangle;
    default: return Clock;
  }
};

const generateTempPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// ====================================================================
// EXTRACTED COMPONENTS (defined outside OnboardingApp)
// ====================================================================

interface DigitalSignatureProps {
  onSign: (signature: string) => void;
  value: string;
}

const DigitalSignature: React.FC<DigitalSignatureProps> = ({ onSign, value }) => {
  const [signature, setSignature] = useState(value || '');
  
  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
      <PenTool className="w-12 h-12 text-gray-400 mx-auto mb-4" />
      <div className="space-y-4">
        <input
          type="text"
          value={signature}
          onChange={(e) => {
            setSignature(e.target.value);
            onSign(e.target.value);
          }}
          placeholder="Type your full name to sign"
          className="w-full p-3 border border-gray-300 rounded-lg text-center text-lg italic"
        />
        {signature && (
          <div className="text-sm text-gray-600">
            Signed on: {new Date().toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

interface AddEmployeeModalProps {
  newEmployeeForm: NewEmployeeForm;
  setNewEmployeeForm: (form: NewEmployeeForm) => void;
  setShowAddEmployeeModal: (show: boolean) => void;
  createEmployeeError: string;
  createEmployeeSuccess: string;
  isCreatingEmployee: boolean;
  handleCreateEmployee: () => void;
  setCreateEmployeeError: (error: string) => void;
  setCreateEmployeeSuccess: (success: string) => void;
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  newEmployeeForm,
  setNewEmployeeForm,
  setShowAddEmployeeModal,
  createEmployeeError,
  createEmployeeSuccess,
  isCreatingEmployee,
  handleCreateEmployee,
  setCreateEmployeeError,
  setCreateEmployeeSuccess
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Add New Employee</h2>
        <button
          onClick={() => {
            setShowAddEmployeeModal(false);
            setCreateEmployeeError('');
            setCreateEmployeeSuccess('');
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
              <input
                type="text"
                value={newEmployeeForm.firstName}
                onChange={(e) => setNewEmployeeForm({...newEmployeeForm, firstName: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
              <input
                type="text"
                value={newEmployeeForm.lastName}
                onChange={(e) => setNewEmployeeForm({...newEmployeeForm, lastName: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
            <input
              type="email"
              value={newEmployeeForm.email}
              onChange={(e) => setNewEmployeeForm({...newEmployeeForm, email: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="john.doe@company.com"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Position *</label>
              <input
                type="text"
                value={newEmployeeForm.position}
                onChange={(e) => setNewEmployeeForm({...newEmployeeForm, position: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Software Engineer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department *</label>
              <select
                value={newEmployeeForm.department}
                onChange={(e) => setNewEmployeeForm({...newEmployeeForm, department: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Department</option>
                <option value="Electric">Electric</option>
                <option value="Engineering">Engineering</option>
                <option value="Gas">Gas</option>
                <option value="Water">Water</option>
                <option value="Energy Supply">Energy Supply</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
            <input
              type="date"
              value={newEmployeeForm.startDate}
              onChange={(e) => setNewEmployeeForm({...newEmployeeForm, startDate: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Temporary Password *</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newEmployeeForm.temporaryPassword}
                onChange={(e) => setNewEmployeeForm({...newEmployeeForm, temporaryPassword: e.target.value})}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter temporary password"
              />
              <button
                type="button"
                onClick={() => setNewEmployeeForm({...newEmployeeForm, temporaryPassword: generateTempPassword()})}
                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                Generate
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Employee will be required to change this on first login</p>
          </div>

          {createEmployeeError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{createEmployeeError}</p>
            </div>
          )}

          {createEmployeeSuccess && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600 font-medium">{createEmployeeSuccess}</p>
              <p className="text-xs text-green-600 mt-1">A welcome email with login instructions has been sent to the employee.</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={() => {
              setShowAddEmployeeModal(false);
              setCreateEmployeeError('');
              setCreateEmployeeSuccess('');
            }}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateEmployee}
            disabled={isCreatingEmployee || !newEmployeeForm.firstName || !newEmployeeForm.lastName || !newEmployeeForm.email || !newEmployeeForm.position || !newEmployeeForm.department || !newEmployeeForm.startDate || !newEmployeeForm.temporaryPassword}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isCreatingEmployee ? (
              <>Creating...</>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Employee
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ====================================================================
// PURGE EMPLOYEE MODAL
// ====================================================================

interface PurgeEmployeeModalProps {
  employees: Employee[];
  setShowPurgeModal: (show: boolean) => void;
  handlePurgeEmployee: (employee: Employee) => void;
  isPurging: boolean;
  purgeError: string;
  purgeSuccess: string;
  setPurgeError: (error: string) => void;
  setPurgeSuccess: (success: string) => void;
}

const PurgeEmployeeModal: React.FC<PurgeEmployeeModalProps> = ({
  employees,
  setShowPurgeModal,
  handlePurgeEmployee,
  isPurging,
  purgeError,
  purgeSuccess,
  setPurgeError,
  setPurgeSuccess
}) => {
  const [purgeStep, setPurgeStep] = useState<'select' | 'confirm'>('select');
  const [selectedPurgeEmployee, setSelectedPurgeEmployee] = useState<Employee | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Sort employees by last name A-Z, then filter by search
  const sortedEmployees = [...employees]
    .filter(emp => emp.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const lastNameA = a.name.split(' ').slice(-1)[0].toLowerCase();
      const lastNameB = b.name.split(' ').slice(-1)[0].toLowerCase();
      return lastNameA.localeCompare(lastNameB);
    });

  const handleClose = () => {
    setShowPurgeModal(false);
    setPurgeStep('select');
    setSelectedPurgeEmployee(null);
    setSearchQuery('');
    setPurgeError('');
    setPurgeSuccess('');
  };

  const handleSelectEmployee = (employee: Employee) => {
    setSelectedPurgeEmployee(employee);
    setPurgeStep('confirm');
    setPurgeError('');
  };

  const handleConfirmPurge = () => {
    if (selectedPurgeEmployee) {
      handlePurgeEmployee(selectedPurgeEmployee);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center">
            {purgeStep === 'confirm' && (
              <button
                onClick={() => {
                  setPurgeStep('select');
                  setSelectedPurgeEmployee(null);
                  setPurgeError('');
                }}
                className="text-gray-400 hover:text-gray-600 mr-3"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Trash2 className="w-5 h-5 text-red-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">
              {purgeStep === 'select' ? 'Purge Employee' : 'Confirm Purge'}
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {purgeStep === 'select' && !purgeSuccess && (
            <div className="p-6">
              {/* Warning Banner */}
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">
                  Purging an employee will <strong>permanently delete</strong> all of their data, uploaded files, and records from the system. This action cannot be undone.
                </p>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Employee List */}
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                Select an employee ({sortedEmployees.length} found)
              </p>

              {sortedEmployees.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">
                    {searchQuery ? 'No employees match your search.' : 'No employees in the system.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {sortedEmployees.map((employee) => {
                    const nameParts = employee.name.split(' ');
                    const lastName = nameParts.slice(-1)[0];
                    const firstName = nameParts.slice(0, -1).join(' ');

                    return (
                      <button
                        key={employee.id}
                        onClick={() => handleSelectEmployee(employee)}
                        className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors text-left group"
                      >
                        <div className="flex items-center">
                          <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center mr-3 group-hover:bg-red-100 transition-colors">
                            <User className="w-4 h-4 text-gray-500 group-hover:text-red-500 transition-colors" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {lastName}, {firstName}
                            </p>
                            <p className="text-xs text-gray-500">{employee.position} — {employee.department}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-red-400 transition-colors" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {purgeStep === 'confirm' && selectedPurgeEmployee && !purgeSuccess && (
            <div className="p-6">
              {/* Employee Summary */}
              <div className="flex items-center p-4 bg-gray-50 rounded-lg mb-5">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <User className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedPurgeEmployee.name}</p>
                  <p className="text-sm text-gray-500">{selectedPurgeEmployee.position} — {selectedPurgeEmployee.department}</p>
                  <p className="text-xs text-gray-400">{selectedPurgeEmployee.email}</p>
                </div>
              </div>

              {/* Danger Warning */}
              <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg mb-5">
                <div className="flex items-center mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                  <h3 className="font-semibold text-red-800">This action is permanent and irreversible</h3>
                </div>
                <p className="text-sm text-red-700 mb-3">
                  You are about to permanently purge <strong>{selectedPurgeEmployee.name}</strong> from the system. The following data will be deleted:
                </p>
                <ul className="text-sm text-red-700 space-y-1 ml-4">
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>User account and login credentials</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>Personal information and contact details</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>All uploaded identification documents and files</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>Tax form submissions (Federal W-4, SC W-4)</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>Direct deposit and banking information</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>Benefits enrollment records</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>Policy acknowledgments and onboarding progress</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>All notifications and audit history</li>
                </ul>
              </div>

              {purgeError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <p className="text-sm text-red-600">{purgeError}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setPurgeStep('select');
                    setSelectedPurgeEmployee(null);
                    setPurgeError('');
                  }}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Go Back
                </button>
                <button
                  onClick={handleConfirmPurge}
                  disabled={isPurging}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isPurging ? (
                    <>Purging...</>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Permanently Purge
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {purgeSuccess && (
            <div className="p-6 text-center py-10">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Employee Purged</h3>
              <p className="text-sm text-gray-600">{purgeSuccess}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface LoginScreenProps {
  loginData: { email: string; password: string };
  setLoginData: (data: { email: string; password: string }) => void;
  loginError: string;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  handleLogin: () => void;
  isLoggingIn: boolean;
  setCurrentView: (view: string) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({
  loginData,
  setLoginData,
  loginError,
  showPassword,
  setShowPassword,
  handleLogin,
  isLoggingIn,
  setCurrentView
}) => (
  <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom right, #E8F1FA, #ffffff, #E8F1FA)' }}>
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-20 mx-auto mb-4" />
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#2D2926' }}>Welcome</h1>
        <p className="text-gray-600">Complete your onboarding process</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="your.email@company.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {loginError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{loginError}</p>
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={isLoggingIn || !loginData.email || !loginData.password}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoggingIn ? 'Signing In...' : 'Sign In'}
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact{' '}
            <a href="mailto:hr-admin@greenwoodcpw.com" className="text-blue-600 hover:text-blue-700 font-medium">
              HR Support
            </a>
          </p>
        </div>
      </div>

<div className="mt-6 text-center space-y-2">
  <button
    onClick={() => setCurrentView('forgot-password')}
    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
  >
    Forgot Password?
  </button>
  <p className="text-xs text-gray-500">Use your credentials to sign in</p>
</div>
    </div>
  </div>
);

interface ForgotPasswordProps {
  resetEmail: string;
  setResetEmail: (email: string) => void;
  setCurrentView: (view: string) => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  resetEmail,
  setResetEmail,
  setCurrentView
}) => {
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setError('');

    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      const data = await response.json();
      if (data.success) {
        setCurrentView('reset-password');
      } else {
        setError(data.message || 'Failed to send reset code. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom right, #E8F1FA, #ffffff, #E8F1FA)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-20 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#2D2926' }}>Forgot Password</h1>
          <p className="text-gray-600">Enter your email and we'll send you a reset code</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="your.email@company.com"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !resetEmail}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Reset Code'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => {
                setResetEmail('');
                setCurrentView('login');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ResetPasswordProps {
  resetEmail: string;
  setResetEmail: (email: string) => void;
  resetCode: string;
  setResetCode: (code: string) => void;
  setCurrentView: (view: string) => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({
  resetEmail,
  setResetEmail,
  resetCode,
  setResetCode,
  setCurrentView
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async () => {
    setError('');

    if (!resetCode || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword) || !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetEmail,
          code: resetCode,
          newPassword: newPassword
        })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          setResetEmail('');
          setResetCode('');
          setSuccess('');
          setCurrentView('login');
        }, 2500);
      } else {
        setError(data.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom right, #E8F1FA, #ffffff, #E8F1FA)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-20 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#2D2926' }}>Reset Password</h1>
          <p className="text-gray-600">Enter the code sent to {resetEmail}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Reset Code</label>
              <input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full text-center text-2xl tracking-widest py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="000000"
                maxLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Confirm your new password"
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-600 mb-1">Password requirements:</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>• At least 8 characters</li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>• One uppercase letter</li>
                <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>• One lowercase letter</li>
                <li className={/\d/.test(newPassword) ? 'text-green-600' : ''}>• One number</li>
                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-600' : ''}>• One special character</li>
              </ul>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600 font-medium">{success}</p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !resetCode || !newPassword || !confirmPassword}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => {
                setResetEmail('');
                setResetCode('');
                setCurrentView('login');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ForceChangePasswordProps {
  accessToken: string;
  onPasswordChanged: () => void;
  handleLogout: () => void;
}

const ForceChangePassword: React.FC<ForceChangePasswordProps> = ({
  accessToken,
  onPasswordChanged,
  handleLogout
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async () => {
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword) || !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from your current password');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      const data = await response.json();
      if (data.success) {
        onPasswordChanged();
      } else {
        setError(data.message || 'Failed to change password. Please try again.');
      }
    } catch (err) {
      console.error('Change password error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom right, #E8F1FA, #ffffff, #E8F1FA)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-20 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#2D2926' }}>Change Your Password</h1>
          <p className="text-gray-600">For security, you must set a new password before continuing</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your temporary password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Confirm your new password"
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-600 mb-1">Password requirements:</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>• At least 8 characters</li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>• One uppercase letter</li>
                <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>• One lowercase letter</li>
                <li className={/\d/.test(newPassword) ? 'text-green-600' : ''}>• One number</li>
                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-600' : ''}>• One special character</li>
              </ul>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Changing Password...' : 'Set New Password'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DashboardProps {
  userData: {
    name: string;
    position: string;
    department: string;
    startDate: string;
    employeeId: string;
  };
  onboardingTasks: OnboardingTask[];
  setCurrentView: (view: string) => void;
  setIsLoggedIn: (loggedIn: boolean) => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  userData,
  onboardingTasks,
  setCurrentView,
  setIsLoggedIn
}) => {

  const [showHRModal, setShowHRModal] = useState(false);

  return (

  <div className="min-h-screen bg-gray-50">
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-3" />
            <h1 className="text-xl font-bold text-gray-900">Onboarding Portal</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">Welcome, {userData.name}</span>
            <button
              onClick={() => setIsLoggedIn(false)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </header>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="rounded-2xl p-8 mb-8 text-white" style={{ background: 'linear-gradient(to right, #0072CE, #001871)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Welcome to the team, {userData.name}!</h2>
            <p className="text-blue-100 mb-4">
              {userData.position} • {userData.department} • Employee ID: {userData.employeeId}
            </p>
            <p className="text-blue-100">
              Start Date: {userData.startDate}
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
      </div>

	<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Onboarding Progress</h3>
        <div className="flex items-center mb-4">
          <div className="flex-1 bg-gray-200 rounded-full h-3 mr-4">
            <div className="bg-blue-600 h-3 rounded-full" style={{ 
		width: `${Math.round((onboardingTasks.filter(task => task.status === 'completed').length / onboardingTasks.length) * 100)}%` 
      		}}></div>
          </div>
          <span className="text-sm font-medium text-gray-700">
		{Math.round((onboardingTasks.filter(task => task.status === 'completed').length / onboardingTasks.length) * 100)}% Complete
	</span>
        </div>
        <p className="text-sm text-gray-600">
          {onboardingTasks.filter(task => task.status === 'completed').length} of {onboardingTasks.length} tasks completed. You're making great progress!
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {onboardingTasks.map((task) => {
          const StatusIcon = getStatusIcon(task.status);
          const TaskIcon = task.icon;
          
          return (
            <div
              key={task.id}
              className={`bg-white rounded-xl shadow-sm border-2 p-6 hover:shadow-md transition-shadow cursor-pointer ${getStatusColor(task.status)}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <TaskIcon className="w-6 h-6 mr-3 text-gray-700" />
                  <StatusIcon className="w-5 h-5" />
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-white bg-opacity-50">
                  {task.status.replace('-', ' ').toUpperCase()}
                </span>
              </div>
              
              <h4 className="font-semibold text-gray-900 mb-2">{task.title}</h4>
              <p className="text-sm text-gray-600 mb-4">{task.description}</p>
              
              {task.progress > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Progress</span>
                    <span>{task.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all" 
                      style={{ width: `${task.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              <button 
                onClick={() => setCurrentView(task.id)}
                className="w-full py-2 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                {task.status === 'completed' ? 'Review' : task.status === 'in-progress' ? 'Continue' : 'Start'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <div className="flex items-start">
          <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-800 mb-1">Need Assistance?</h4>
            <p className="text-yellow-700 text-sm mb-3">
              If you have questions or encounter any issues during the onboarding process, our HR team is here to help.
            </p>
            <div className="flex flex-wrap gap-3">
		<button 
    		  onClick={() => setShowHRModal(true)}
    		  className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
  		>
    		  Contact HR
  		</button>
              <button className="bg-white hover:bg-yellow-50 text-yellow-800 text-sm font-medium px-4 py-2 rounded-lg border border-yellow-300 transition-colors">
                View FAQ
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

{/* HR Contact Modal */}
{showHRModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
            <Phone className="w-5 h-5 text-yellow-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Contact HR</h3>
        </div>
        <button
          onClick={() => setShowHRModal(false)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center p-4 bg-gray-50 rounded-lg">
          <Phone className="w-5 h-5 text-gray-600 mr-3" />
          <div>
            <p className="text-sm text-gray-500">Phone</p>
            <p className="text-lg font-medium text-gray-900">(864) 942-8107</p>
          </div>
        </div>

        <div className="flex items-center p-4 bg-gray-50 rounded-lg">
          <Mail className="w-5 h-5 text-gray-600 mr-3" />
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="text-lg font-medium text-gray-900">hr-admin@greenwoodcpw.com</p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={() => setShowHRModal(false)}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

  </div>
);
};

interface HRAdminPanelProps {
  employees: Employee[];
  setShowAddEmployeeModal: (show: boolean) => void;
  setShowPurgeModal: (show: boolean) => void;
  setIsLoggedIn: (loggedIn: boolean) => void;
  setSelectedEmployee: (employee: Employee) => void;
  setCurrentView: (view: string) => void;
}

const HRAdminPanel: React.FC<HRAdminPanelProps> = ({
  employees,
  setShowAddEmployeeModal,
  setShowPurgeModal,
  setIsLoggedIn,
  setSelectedEmployee,
  setCurrentView
}) => (
  <div className="min-h-screen bg-gray-50">
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-3" />
            <h1 className="text-xl font-bold text-gray-900">HR Admin Panel</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowPurgeModal(true)}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Purge Employee
            </button>
            <button
              onClick={() => setShowAddEmployeeModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </button>
            <button
              onClick={() => setIsLoggedIn(false)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </header>

<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
  {/* Active Onboarding */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-center">
      <Users className="w-8 h-8 text-blue-600 mr-3" />
      <div>
        <p className="text-2xl font-bold text-gray-900">
          {employees.filter(emp => emp.progress < 100).length}
        </p>
        <p className="text-gray-600">Active Onboarding</p>
      </div>
    </div>
  </div>
  
  {/* Completed Today */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-center">
      <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
      <div>
        <p className="text-2xl font-bold text-gray-900">
          {(() => {
            const today = new Date().toISOString().split('T')[0];
            return employees.reduce((total, emp) => {
              return total + Object.values(emp.tasks).filter(task => {
                const taskDate = task.updatedAt ? new Date(task.updatedAt).toISOString().split('T')[0] : null;
                return task.status === 'completed' && taskDate === today;
              }).length;
            }, 0);
          })()}
        </p>
        <p className="text-gray-600">Tasks Completed Today</p>
      </div>
    </div>
  </div>
    </div>
  </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Employee Onboarding Status</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Employee</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Department</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Start Date</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Progress</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="flex items-center">
                      <UserCheck className="w-8 h-8 text-gray-400 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">{employee.name}</p>
                        <p className="text-sm text-gray-600">{employee.position}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-900">{employee.department}</td>
                  <td className="py-4 px-4 text-gray-600">
  			{new Date(employee.startDate).toLocaleDateString()}
		   </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${employee.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{employee.progress}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      employee.progress === 100 ? 'bg-green-100 text-green-800' :
                      employee.progress > 50 ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {employee.progress === 100 ? 'Complete' :
                       employee.progress > 50 ? 'In Progress' : 'Getting Started'}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <button 
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setCurrentView('employee-details');
                      }}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

</div>
);

interface EmployeeDetailsViewProps {
  selectedEmployee: Employee;
  setCurrentView: (view: string) => void;
  setIsLoggedIn: (loggedIn: boolean) => void;
  accessToken: string;
}

// Read-only field display helper
const ReadOnlyField: React.FC<{ label: string; value?: string | boolean; type?: 'text' | 'boolean' | 'ssn' }> = ({ label, value, type = 'text' }) => {
  const isEmpty = value === undefined || value === null || value === '';
  
  if (type === 'boolean') {
    return (
      <div className="py-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <div className="mt-1">
          {value ? (
            <span className="inline-flex items-center text-sm text-green-700 font-medium">
              <CheckCircle className="w-4 h-4 mr-1.5" />
              Acknowledged
            </span>
          ) : (
            <span className="inline-flex items-center text-sm text-amber-600 italic">
              <AlertTriangle className="w-4 h-4 mr-1.5" />
              Not acknowledged
            </span>
          )}
        </div>
      </div>
    );
  }

  if (type === 'ssn' && !isEmpty) {
    const ssnStr = String(value);
    const masked = ssnStr.length >= 4 ? '•••-••-' + ssnStr.slice(-4) : ssnStr;
    return (
      <div className="py-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <p className="mt-1 text-sm text-gray-900 font-mono">{masked}</p>
      </div>
    );
  }

  return (
    <div className="py-2">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      {isEmpty ? (
        <p className="mt-1 text-sm text-amber-500 italic flex items-center">
          <span className="mr-1">—</span> Not provided
        </p>
      ) : (
        <p className="mt-1 text-sm text-gray-900">{String(value)}</p>
      )}
    </div>
  );
};

// Filing status display helpers
const getFederalFilingStatusLabel = (status: string) => {
  switch (status) {
    case 'single_separate': return 'Single or Married filing separately';
    case 'married_jointly': return 'Married filing jointly or Qualifying surviving spouse';
    case 'head_of_household': return 'Head of household';
    default: return '';
  }
};

const getSCFilingStatusLabel = (status: string) => {
  switch (status) {
    case 'single': return 'Single';
    case 'married': return 'Married';
    case 'married_higher_single': return 'Married, but withhold at higher Single rate';
    default: return '';
  }
};

const getSCExemptReasonLabel = (reason: string) => {
  switch (reason) {
    case 'no_liability': return 'No SC income tax liability last year and expects none this year';
    case 'military': return 'Qualifying spouse of a military servicemember';
    default: return '';
  }
};

const EmployeeDetailsView: React.FC<EmployeeDetailsViewProps> = ({
  selectedEmployee,
  setCurrentView,
  setIsLoggedIn,
  accessToken
}) => {
  // Accordion state
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());

  // Send Reminder state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderSubject, setReminderSubject] = useState('Onboarding Reminder — Greenwood CPW');
  const [reminderMessage, setReminderMessage] = useState('');
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [lastReminderSent, setLastReminderSent] = useState<string | null>(null);

  // Export Report state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelections, setExportSelections] = useState({
    summary: false,
    identification: false,
    taxForms: false,
    directDeposit: false
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const toggleAccordion = (taskId: string) => {
    setOpenAccordions(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  // Extract data from employee tasks
  const personalData = selectedEmployee.tasks.personalInfo.data || {};
  const identificationData = selectedEmployee.tasks.identification.data || {};
  const federalW4 = selectedEmployee.tasks.taxForms.data?.federalW4 || {};
  const scW4 = selectedEmployee.tasks.taxForms.data?.scW4 || {};
  const directDepositInfo = selectedEmployee.tasks.directDeposit.data || {};
  const benefitsInfo = selectedEmployee.tasks.benefits.data || {};
  const handbookInfo = selectedEmployee.tasks.policies.data || {};

  // Send Reminder handler
  const handleSendReminder = async () => {
    setIsSendingReminder(true);
    setReminderError('');
    try {
      const response = await fetch('/api/notifications/send-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          employeeId: selectedEmployee.id,
          recipientEmail: selectedEmployee.email,
          recipientName: selectedEmployee.name,
          subject: reminderSubject,
          message: reminderMessage
        })
      });
      const data = await response.json();
      if (data.success) {
        setReminderSent(true);
        setLastReminderSent(new Date().toLocaleString());
        setTimeout(() => {
          setShowReminderModal(false);
          setShowReminderConfirm(false);
          setReminderSent(false);
          setReminderSubject('Onboarding Reminder — Greenwood CPW');
          setReminderMessage('');
        }, 2500);
      } else {
        setReminderError(data.message || 'Failed to send reminder. Please try again.');
        setShowReminderConfirm(false);
      }
    } catch (error) {
      console.error('Send reminder error:', error);
      setReminderError('Unable to connect to server. Please try again.');
      setShowReminderConfirm(false);
    } finally {
      setIsSendingReminder(false);
    }
  };

  // Export Report handler
  const handleExport = async () => {
    setIsExporting(true);
    setExportError('');
    try {
      const response = await fetch('/api/reports/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          employeeId: selectedEmployee.id,
          sections: exportSelections
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedEmployee.name.replace(/\s+/g, '_')}_Onboarding_Report.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setShowExportModal(false);
        setExportSelections({ summary: false, identification: false, taxForms: false, directDeposit: false });
      } else {
        const data = await response.json();
        setExportError(data.message || 'Failed to generate report. Please try again.');
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportError('Unable to connect to server. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const hasExportSelection = Object.values(exportSelections).some(v => v);

  // Accordion content renderers
  const renderPersonalInfoContent = () => (
    <div className="px-6 py-5 border-t border-gray-200 bg-white">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
        <ReadOnlyField label="First Name" value={personalData.firstName} />
        <ReadOnlyField label="Last Name" value={personalData.lastName} />
        <ReadOnlyField label="Phone Number" value={personalData.phone} />
        <ReadOnlyField label="Email Address" value={personalData.email} />
        <div className="md:col-span-2">
          <ReadOnlyField label="Street Address" value={personalData.address} />
        </div>
        <ReadOnlyField label="City" value={personalData.city} />
        <div className="grid grid-cols-2 gap-4">
          <ReadOnlyField label="State" value={personalData.state} />
          <ReadOnlyField label="ZIP Code" value={personalData.zipCode} />
        </div>
        <ReadOnlyField label="Emergency Contact" value={personalData.emergencyContact} />
        <ReadOnlyField label="Emergency Phone" value={personalData.emergencyPhone} />
      </div>
    </div>
  );

  const renderIdentificationContent = () => {
    const files = identificationData.files || {};
    const hasAnyFiles = files.driversLicense || files.socialSecurity || files.other;

    const renderFilePreview = (file: any, label: string) => {
      if (!file) {
        return (
          <div className="border border-dashed border-amber-300 bg-amber-50 rounded-lg p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{label}</p>
            <p className="text-sm text-amber-500 italic flex items-center">
              <span className="mr-1">—</span> Not uploaded
            </p>
          </div>
        );
      }

	const fileType = file.file_type || file.type; // Check both property names
	const isImage = fileType && (fileType.startsWith('image/') || /\.(jpg|jpeg|png|gif)$/i.test(file.name || file.file_name));
	const isPdf = fileType === 'application/pdf' || /\.pdf$/i.test(file.name || file.file_name);
	const fileUrl = file.file_data || file.url || `http://localhost:3001/uploads/${file.name || file.file_name}`;

      return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center">
              <FileCheck className="w-4 h-4 text-green-600 mr-2" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
            </div>
            <span className="text-xs text-gray-400">{file.uploadDate || ''}</span>
          </div>
          <div className="p-2 bg-gray-100" style={{ maxHeight: '400px' }}>
            {isImage && (
              <img src={fileUrl} alt={label} className="w-full h-auto max-h-[380px] object-contain rounded" />
            )}
            {isPdf && (
              <iframe src={fileUrl} title={label} className="w-full rounded border border-gray-300 bg-white" style={{ height: '380px' }} />
            )}
            {!isImage && !isPdf && (
              <div className="flex items-center justify-center py-8">
                <FileText className="w-8 h-8 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-700">{file.name}</p>
                  <p className="text-xs text-gray-400">Preview not available for this file type</p>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="px-6 py-5 border-t border-gray-200 bg-white">
        {!hasAnyFiles ? (
          <p className="text-sm text-amber-500 italic flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2" />
            No identification documents have been uploaded yet.
          </p>
        ) : (
          <div className="space-y-4">
            {renderFilePreview(files.driversLicense, "Driver's License")}
            {renderFilePreview(files.socialSecurity, "Social Security Card")}
            {files.other && renderFilePreview(files.other, "Other Identification")}
            {!files.driversLicense && !files.other && null}
          </div>
        )}
      </div>
    );
  };

  const renderTaxFormsContent = () => {
    const hasFederalData = federalW4.firstName || federalW4.lastName || federalW4.filingStatus;
    const hasSCData = scW4.firstName || scW4.lastName || scW4.filingStatus;

    return (
      <div className="px-6 py-5 border-t border-gray-200 bg-white space-y-6">
        {/* Federal W-4 */}
        <div>
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <h5 className="font-semibold text-gray-800">Federal W-4 (2026)</h5>
          </div>
          {!hasFederalData ? (
            <p className="text-sm text-amber-500 italic flex items-center ml-11">
              <span className="mr-1">—</span> No federal tax form data has been submitted yet.
            </p>
          ) : (
            <div className="ml-11 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
                <ReadOnlyField label="First Name" value={federalW4.firstName} />
                <ReadOnlyField label="M.I." value={federalW4.middleInitial} />
                <ReadOnlyField label="Last Name" value={federalW4.lastName} />
              </div>
              <ReadOnlyField label="Address" value={federalW4.address} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
                <ReadOnlyField label="City" value={federalW4.city} />
                <ReadOnlyField label="State" value={federalW4.state} />
                <ReadOnlyField label="ZIP Code" value={federalW4.zipCode} />
              </div>
              <ReadOnlyField label="Social Security Number" value={federalW4.ssn} type="ssn" />
              <ReadOnlyField label="Filing Status" value={getFederalFilingStatusLabel(federalW4.filingStatus)} />
              <ReadOnlyField label="Multiple Jobs / Spouse Works (Step 2c)" value={federalW4.multipleJobsCheckbox ? 'Yes — Checked' : 'No'} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
                <ReadOnlyField label="Qualifying Children (×$2,200)" value={federalW4.qualifyingChildren} />
                <ReadOnlyField label="Other Dependents (×$500)" value={federalW4.otherDependents} />
                <ReadOnlyField label="Other Credits" value={federalW4.otherCredits ? `$${federalW4.otherCredits}` : ''} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
                <ReadOnlyField label="Other Income (4a)" value={federalW4.otherIncome ? `$${federalW4.otherIncome}` : ''} />
                <ReadOnlyField label="Deductions (4b)" value={federalW4.deductions ? `$${federalW4.deductions}` : ''} />
                <ReadOnlyField label="Extra Withholding (4c)" value={federalW4.extraWithholding ? `$${federalW4.extraWithholding}` : ''} />
              </div>
              {federalW4.claimExempt && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <span className="text-sm text-yellow-800 font-medium">⚠ Employee claims exemption from withholding for 2026</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 pt-2 border-t border-gray-100">
                <ReadOnlyField label="Signature" value={federalW4.signature} />
                <ReadOnlyField label="Date Signed" value={federalW4.signatureDate} />
              </div>
            </div>
          )}
        </div>

        {/* SC W-4 */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
              <FileText className="w-4 h-4 text-green-600" />
            </div>
            <h5 className="font-semibold text-gray-800">South Carolina W-4 (2026)</h5>
          </div>
          {!hasSCData ? (
            <p className="text-sm text-amber-500 italic flex items-center ml-11">
              <span className="mr-1">—</span> No SC tax form data has been submitted yet.
            </p>
          ) : (
            <div className="ml-11 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
                <ReadOnlyField label="First Name" value={scW4.firstName} />
                <ReadOnlyField label="M.I." value={scW4.middleInitial} />
                <ReadOnlyField label="Last Name" value={scW4.lastName} />
              </div>
              <ReadOnlyField label="Address" value={scW4.address} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
                <ReadOnlyField label="City" value={scW4.city} />
                <ReadOnlyField label="State" value={scW4.state} />
                <ReadOnlyField label="ZIP Code" value={scW4.zipCode} />
              </div>
              <ReadOnlyField label="Social Security Number" value={scW4.ssn} type="ssn" />
              <ReadOnlyField label="Filing Status (Line 3)" value={getSCFilingStatusLabel(scW4.filingStatus)} />
              <ReadOnlyField label="Last Name Differs from SS Card (Line 4)" value={scW4.lastNameDiffers ? 'Yes' : 'No'} />
              <ReadOnlyField label="Total Allowances (Line 5)" value={scW4.totalAllowances} />
              <ReadOnlyField label="Additional Withholding (Line 6)" value={scW4.additionalWithholding ? `$${scW4.additionalWithholding}` : ''} />
              {scW4.claimExempt && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg space-y-1">
                  <span className="text-sm text-yellow-800 font-medium">⚠ Employee claims exemption from SC withholding</span>
                  {scW4.exemptReason && (
                    <p className="text-sm text-yellow-700">Reason: {getSCExemptReasonLabel(scW4.exemptReason)}</p>
                  )}
                  {scW4.exemptReason === 'military' && scW4.militaryDomicileState && (
                    <p className="text-sm text-yellow-700">Servicemember domicile state: {scW4.militaryDomicileState}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 pt-2 border-t border-gray-100">
                <ReadOnlyField label="Signature" value={scW4.signature} />
                <ReadOnlyField label="Date Signed" value={scW4.signatureDate} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDirectDepositContent = () => {
    const hasData = directDepositInfo.depositoryName || directDepositInfo.accountNumber;
    const ddFiles = directDepositInfo.files || {};

    return (
      <div className="px-6 py-5 border-t border-gray-200 bg-white">
        {!hasData ? (
          <p className="text-sm text-amber-500 italic flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2" />
            No direct deposit information has been submitted yet.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
              <ReadOnlyField label="Depository Name" value={directDepositInfo.depositoryName} />
              <div className="grid grid-cols-2 gap-4">
                <ReadOnlyField label="City" value={directDepositInfo.depositoryCity} />
                <ReadOnlyField label="State" value={directDepositInfo.depositoryState} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
              <ReadOnlyField label="Transit/ABA Routing Number" value={directDepositInfo.transitABANumber} />
              <ReadOnlyField label="Account Number" value={directDepositInfo.accountNumber} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
              <ReadOnlyField label="Account Type" value={directDepositInfo.accountType ? directDepositInfo.accountType.charAt(0).toUpperCase() + directDepositInfo.accountType.slice(1) : ''} />
              <ReadOnlyField label="Deposit Amount" value={
                directDepositInfo.depositType === 'full' ? 'Full amount' :
                directDepositInfo.depositType === 'specific' ? `Specific amount: $${directDepositInfo.specificAmount || ''}` : ''
              } />
            </div>
            <ReadOnlyField label="Authorization Agreement" value={directDepositInfo.authorizationAcknowledged} type="boolean" />
            <ReadOnlyField label="Employee Name (Printed)" value={directDepositInfo.employeeName} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 pt-2 border-t border-gray-100">
              <ReadOnlyField label="Signature" value={directDepositInfo.signature} />
              <ReadOnlyField label="Date Signed" value={directDepositInfo.signatureDate} />
            </div>

            {ddFiles.voidedCheck && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Voided Check / Deposit Slip</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <FileCheck className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-gray-700">{ddFiles.voidedCheck.name}</span>
                    <span className="ml-auto text-xs text-gray-400">{ddFiles.voidedCheck.uploadDate}</span>
                  </div>
                  <div className="p-2 bg-gray-100" style={{ maxHeight: '300px' }}>
                    {ddFiles.voidedCheck.type && ddFiles.voidedCheck.type.startsWith('image/') ? (
                      <img src={ddFiles.voidedCheck.url || `http://localhost:3001/uploads/${ddFiles.voidedCheck.name}`} alt="Voided Check" className="w-full h-auto max-h-[280px] object-contain rounded" />
                    ) : (
                      <iframe src={ddFiles.voidedCheck.url || `http://localhost:3001/uploads/${ddFiles.voidedCheck.name}`} title="Voided Check" className="w-full rounded border border-gray-300 bg-white" style={{ height: '280px' }} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderBenefitsContent = () => (
    <div className="px-6 py-5 border-t border-gray-200 bg-white">
      <div className="space-y-3">
        <ReadOnlyField label="2026 Benefit Guide — Greenwood CPW" value={benefitsInfo.benefitGuideAcknowledged} type="boolean" />
        <ReadOnlyField label="2026 SCRS Retirement Handbook" value={benefitsInfo.scrsHandbookAcknowledged} type="boolean" />
      </div>
    </div>
  );

  const renderHandbookContent = () => (
    <div className="px-6 py-5 border-t border-gray-200 bg-white">
      <ReadOnlyField label="Greenwood CPW Personnel Handbook" value={handbookInfo.handbookAcknowledged} type="boolean" />
    </div>
  );

  const accordionContentMap: Record<string, () => React.ReactNode> = {
    personalInfo: renderPersonalInfoContent,
    identification: renderIdentificationContent,
    taxForms: renderTaxFormsContent,
    directDeposit: renderDirectDepositContent,
    benefits: renderBenefitsContent,
    policies: renderHandbookContent
  };

  const taskDetails = [
    { 
      id: 'personalInfo', 
      title: 'Personal Information', 
      status: selectedEmployee.tasks.personalInfo.status,
      icon: User,
      details: 'Employee contact information, emergency contacts, and basic personal details'
    },
    { 
      id: 'identification', 
      title: 'Identification Upload', 
      status: selectedEmployee.tasks.identification.status,
      icon: Upload,
      details: 'Government-issued ID, Social Security card, work authorization documents'
    },
    { 
      id: 'taxForms', 
      title: 'Tax Forms (W-4)', 
      status: selectedEmployee.tasks.taxForms.status,
      icon: FileText,
      details: 'Federal and state tax withholding forms, filing status, dependents'
    },
    { 
      id: 'directDeposit', 
      title: 'Direct Deposit', 
      status: selectedEmployee.tasks.directDeposit.status,
      icon: Landmark,
      details: 'Payroll direct deposit authorization, bank account and routing information'
    },
    { 
      id: 'benefits', 
      title: 'Employee Benefits Review', 
      status: selectedEmployee.tasks.benefits.status,
      icon: Shield,
      details: 'Review and acknowledgment of benefits guide and SCRS retirement handbook'
    },
    { 
      id: 'policies', 
      title: 'Employee Handbook Review', 
      status: selectedEmployee.tasks.policies.status,
      icon: PenTool,
      details: 'Review and acknowledgment of the Greenwood CPW Personnel Handbook'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => setCurrentView('hr-admin')}
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Back to Admin Panel
              </button>
              <Settings className="w-8 h-8 text-blue-600 mr-3 hidden" />
              <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-3" />
              <h1 className="text-xl font-bold text-gray-900">Employee Details</h1>
            </div>
            <button
              onClick={() => setIsLoggedIn(false)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Employee Profile Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <UserCheck className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedEmployee.name}</h2>
                <p className="text-gray-600">{selectedEmployee.position} • {selectedEmployee.department}</p>
                <p className="text-sm text-gray-500">Start Date: {new Date(selectedEmployee.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{selectedEmployee.progress}%</div>
              <div className="text-sm text-gray-600">Complete</div>
              {lastReminderSent && (
                <div className="text-xs text-gray-400 mt-2">Last reminder: {lastReminderSent}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Mail className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">Email</span>
              </div>
              <p className="text-gray-900">{selectedEmployee.email}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">Start Date</span>
              </div>
              <p className="text-sm text-gray-500">Start Date: {new Date(selectedEmployee.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Users className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">Department</span>
              </div>
              <p className="text-gray-900">{selectedEmployee.department}</p>
            </div>
          </div>
        </div>

        {/* Task Progress with Accordions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Task Progress</h3>
          
          <div className="space-y-3">
            {taskDetails.map((task) => {
              const StatusIcon = getStatusIcon(task.status);
              const TaskIcon = task.icon;
              const isOpen = openAccordions.has(task.id);
              const renderContent = accordionContentMap[task.id];
              
              return (
                <div key={task.id} className={`border rounded-lg overflow-hidden transition-all ${getStatusColor(task.status)}`}>
                  {/* Accordion Header — Clickable */}
                  <button
                    onClick={() => toggleAccordion(task.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-opacity-75 transition-colors text-left"
                  >
                    <div className="flex items-center flex-1">
                      <TaskIcon className="w-6 h-6 mr-3 text-gray-700" />
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <h4 className="font-semibold text-gray-900 mr-3">{task.title}</h4>
                          <StatusIcon className="w-5 h-5" />
                          <span className="ml-2 text-xs font-semibold px-2 py-1 rounded-full bg-white bg-opacity-50">
                            {task.status.replace('-', ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{task.details}</p>
                      </div>
                    </div>
                    <div className="flex items-center ml-4">
                      <div className="text-right mr-4">
                        {task.status === 'completed' && (
                          <div className="text-sm text-green-600 font-medium">✓ Completed</div>
                        )}
                        {task.status === 'in-progress' && (
                          <div className="text-sm text-blue-600 font-medium">⏳ In Progress</div>
                        )}
                        {task.status === 'pending' && (
                          <div className="text-sm text-gray-500 font-medium">⏸️ Not Started</div>
                        )}
                      </div>
                      {isOpen ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Accordion Content */}
                  {isOpen && renderContent && renderContent()}
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setShowReminderModal(true)}
              className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Reminder
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center justify-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* ============ SEND REMINDER MODAL ============ */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Send Reminder</h2>
              <button
                onClick={() => {
                  setShowReminderModal(false);
                  setShowReminderConfirm(false);
                  setReminderSent(false);
                  setReminderError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {!showReminderConfirm && !reminderSent ? (
                <>
                  {/* Recipient (read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    <div className="flex items-center p-3 bg-gray-100 rounded-lg">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">{selectedEmployee.email}</span>
                      <span className="ml-auto text-xs text-gray-400">{selectedEmployee.name}</span>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={reminderSubject}
                      onChange={(e) => setReminderSubject(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Email subject"
                    />
                  </div>

                  {/* Message Body */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      value={reminderMessage}
                      onChange={(e) => setReminderMessage(e.target.value)}
                      rows={8}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="Type your reminder message here..."
                    />
                  </div>

                  {reminderError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{reminderError}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowReminderModal(false);
                        setReminderError('');
                      }}
                      className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowReminderConfirm(true)}
                      disabled={!reminderSubject.trim() || !reminderMessage.trim()}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Review & Send
                    </button>
                  </div>
                </>
              ) : reminderSent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Reminder Sent!</h3>
                  <p className="text-sm text-gray-600">
                    Your reminder has been sent to {selectedEmployee.email}.
                  </p>
                </div>
              ) : (
                /* Confirmation Step */
                <div className="space-y-5">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-medium mb-1">Confirm send</p>
                    <p className="text-sm text-amber-700">
                      Are you sure you want to send this reminder to <strong>{selectedEmployee.email}</strong>?
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Subject</span>
                      <p className="text-sm text-gray-900 mt-0.5">{reminderSubject}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Message</span>
                      <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{reminderMessage}</p>
                    </div>
                  </div>

                  {reminderError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{reminderError}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      onClick={() => setShowReminderConfirm(false)}
                      className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={handleSendReminder}
                      disabled={isSendingReminder}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 flex items-center"
                    >
                      {isSendingReminder ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Confirm & Send
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ EXPORT REPORT MODAL ============ */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="border-b border-gray-200 p-6 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Export Report</h2>
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setExportError('');
                  setExportSelections({ summary: false, identification: false, taxForms: false, directDeposit: false });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 mb-2">
                Select one or more sections to include in the PDF export for <strong>{selectedEmployee.name}</strong>.
              </p>

              {/* Export Options */}
              {[
                { key: 'summary', title: 'Summary', desc: 'Employee profile overview with condensed task data and completion status', icon: FileText },
                { key: 'identification', title: 'Identification Upload', desc: 'Print-ready identification document images with labels', icon: Upload },
                { key: 'taxForms', title: 'Tax Forms (W-4)', desc: 'Populated Federal W-4 and SC W-4 PDF forms', icon: FileText },
                { key: 'directDeposit', title: 'Direct Deposit', desc: 'Populated Direct Deposit Authorization PDF form', icon: Landmark }
              ].map(({ key, title, desc, icon: Icon }) => (
                <label
                  key={key}
                  className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-colors border ${
                    (exportSelections as any)[key]
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={(exportSelections as any)[key]}
                    onChange={(e) => setExportSelections(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <Icon className="w-4 h-4 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{title}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 ml-6">{desc}</p>
                  </div>
                </label>
              ))}

              {exportError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{exportError}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowExportModal(false);
                    setExportError('');
                    setExportSelections({ summary: false, identification: false, taxForms: false, directDeposit: false });
                  }}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={!hasExportSelection || isExporting}
                  className="px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isExporting ? (
                    <>Generating...</>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Generate PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface PersonalInfoFormProps {
  personalInfoData: PersonalInfo;
  setPersonalInfoData: (data: PersonalInfo) => void;
  setCurrentView: (view: string) => void;
  fetchUserProfile: (token: string) => Promise<void>;
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
  personalInfoData,
  setPersonalInfoData,
  setCurrentView,
  fetchUserProfile
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    
    try {
      const response = await fetch('/api/personal-info/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify(personalInfoData)
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('Save successful, refreshing profile...');
        setSaveSuccess(true);
         // Refresh user data to update task status  
        await fetchUserProfile(localStorage.getItem('accessToken') || '');
        console.log('fetchUserProfile completed');
        // Show success message briefly, then go back
        setTimeout(() => {
          setCurrentView('dashboard');
        }, 2000);
      } else {
        setSaveError(data.message || 'Failed to save information');
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveError('Unable to connect to server. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Add validation
  const canSave = personalInfoData.firstName.trim() !== '' && 
                  personalInfoData.lastName.trim() !== '' && 
                  personalInfoData.email.trim() !== '' && 
                  personalInfoData.phone.trim() !== '';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center mb-6">
            <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-4" />
            <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
              <input
                type="text"
                value={personalInfoData.firstName}
                onChange={(e) => setPersonalInfoData({...personalInfoData, firstName: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your first name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
              <input
                type="text"
                value={personalInfoData.lastName}
                onChange={(e) => setPersonalInfoData({...personalInfoData, lastName: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your last name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={personalInfoData.phone}
                  onChange={(e) => setPersonalInfoData({...personalInfoData, phone: e.target.value})}
                  className="w-full pl-10 pr-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={personalInfoData.email}
                  onChange={(e) => setPersonalInfoData({...personalInfoData, email: e.target.value})}
                  className="w-full pl-10 pr-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={personalInfoData.address}
                  onChange={(e) => setPersonalInfoData({...personalInfoData, address: e.target.value})}
                  className="w-full pl-10 pr-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="123 Main Street"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact</label>
              <input
                type="text"
                value={personalInfoData.emergencyContact}
                onChange={(e) => setPersonalInfoData({...personalInfoData, emergencyContact: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Emergency contact name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Phone</label>
              <input
                type="tel"
                value={personalInfoData.emergencyPhone}
                onChange={(e) => setPersonalInfoData({...personalInfoData, emergencyPhone: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          
          {/* Error and Success Messages */}
          {saveError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{saveError}</p>
            </div>
          )}

          {saveSuccess && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Information saved successfully! Returning to dashboard...</p>
            </div>
          )}
          
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !canSave}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Information'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface IdentificationUploadProps {
  identificationFiles: IdentificationFiles;
  handleIdentificationUpload: (event: React.ChangeEvent<HTMLInputElement>, category: keyof IdentificationFiles) => void;
  handleIdentificationSubmit: () => void;
  setCurrentView: (view: string) => void;
  fetchUserProfile: (token: string) => Promise<void>;
}

const IdentificationUploadZone: React.FC<{
  label: string;
  description: string;
  inputId: string;
  file: UploadedFile | null;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  required?: boolean;
}> = ({ label, description, inputId, file, onUpload, onRemove, required }) => (
  <div className={`border-2 rounded-lg p-6 transition-colors ${file ? 'border-green-300 bg-green-50' : 'border-dashed border-gray-300 bg-white'}`}>
    <div className="flex items-start justify-between mb-3">
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </h3>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
      {file ? (
        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
      ) : (
        <Upload className="w-6 h-6 text-gray-400 flex-shrink-0" />
      )}
    </div>

    {file ? (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
        <div className="flex items-center min-w-0">
          <FileCheck className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{file.name}</p>
            <p className="text-xs text-gray-500">Uploaded {file.uploadDate}</p>
          </div>
        </div>
        <button
          onClick={onRemove}
          className="ml-3 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
          title="Remove file"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    ) : (
      <div className="text-center pt-2">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={onUpload}
          className="hidden"
          id={inputId}
        />
        <label
          htmlFor={inputId}
          className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
        >
          <Upload className="w-4 h-4 mr-2" />
          Choose File
        </label>
        <p className="text-xs text-gray-400 mt-2">PDF, JPG, or PNG accepted</p>
      </div>
    )}
  </div>
);

const IdentificationUpload: React.FC<IdentificationUploadProps> = ({
  identificationFiles,
  handleIdentificationUpload,
  handleIdentificationSubmit,
  setCurrentView,
  fetchUserProfile
}) => {
  const canSubmit = identificationFiles.driversLicense !== null && identificationFiles.socialSecurity !== null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <div className="flex items-center mb-3">
              <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">Upload Identification</h2>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Please upload your identification documents below. Driver's License and Social Security Card are required.
            </p>
          </div>

          <div className="space-y-5 mb-8">
            <IdentificationUploadZone
              label="Driver's License"
              description="Upload a clear photo or scan of your valid driver's license (front)"
              inputId="upload-drivers-license"
              file={identificationFiles.driversLicense}
              onUpload={(e) => handleIdentificationUpload(e, 'driversLicense')}
              onRemove={() => {}}
              required
            />

            <IdentificationUploadZone
              label="Social Security Card"
              description="Upload a clear photo or scan of your Social Security card"
              inputId="upload-social-security"
              file={identificationFiles.socialSecurity}
              onUpload={(e) => handleIdentificationUpload(e, 'socialSecurity')}
              onRemove={() => {}}
              required
            />

            <IdentificationUploadZone
              label="Other"
              description="Optional — upload any additional identification (passport, state ID, work authorization, etc.)"
              inputId="upload-other"
              file={identificationFiles.other}
              onUpload={(e) => handleIdentificationUpload(e, 'other')}
              onRemove={() => {}}
            />
          </div>

          {!canSubmit && (
            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Please upload both your Driver's License and Social Security Card before submitting.
              </p>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
            <button
              onClick={handleIdentificationSubmit}
              disabled={!canSubmit}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Documents
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface W4FormProps {
  federalW4Data: FederalW4Data;
  setFederalW4Data: (data: FederalW4Data) => void;
  scW4Data: SCW4Data;
  setScW4Data: (data: SCW4Data) => void;
  setCurrentView: (view: string) => void;
  fetchUserProfile: (token: string) => Promise<void>;
}

const W4Form: React.FC<W4FormProps> = ({
  federalW4Data,
  setFederalW4Data,
  scW4Data,
  setScW4Data,
  setCurrentView,
  fetchUserProfile
}) => {
  const inputClass = "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const sectionHeaderClass = "text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200";
  const radioLabelClass = "flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors";

  // Auto-populate SC W-4 from Federal data
  const handleFederalPersonalChange = (field: string, value: string) => {
    setFederalW4Data({ ...federalW4Data, [field]: value });
    if (['firstName', 'middleInitial', 'lastName', 'address', 'city', 'state', 'zipCode', 'ssn'].includes(field)) {
      const scField = field as keyof SCW4Data;
      if (scW4Data[scField] === (federalW4Data as any)[field] || scW4Data[scField] === '') {
        setScW4Data({ ...scW4Data, [field]: value });
      }
    }
  };

const [isSaving, setIsSaving] = useState(false);
const [saveError, setSaveError] = useState('');
const [saveSuccess, setSaveSuccess] = useState(false);

const handleTaxFormsSubmit = async () => {
  setIsSaving(true);
  setSaveError('');
  
  try {
    const response = await fetch('/api/tax-forms/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      },
      body: JSON.stringify({
        federalW4Data,
        scW4Data
      })
    });

    const data = await response.json();
    
    if (data.success) {
      setSaveSuccess(true);
      // Refresh user data to update task status
      const token = localStorage.getItem('accessToken');
      if (token) {
        await fetchUserProfile(token);
      }
      // Show success message briefly, then go back
      setTimeout(() => {
        setCurrentView('dashboard');
      }, 2000);
    } else {
      setSaveError(data.message || 'Failed to save tax forms');
    }
  } catch (error) {
    console.error('Tax forms save error:', error);
    setSaveError('Unable to connect to server. Please try again.');
  } finally {
    setIsSaving(false);
  }
};

  // Calculate dependents total
  const childrenCount = parseInt(federalW4Data.qualifyingChildren) || 0;
  const otherDepsCount = parseInt(federalW4Data.otherDependents) || 0;
  const childrenAmount = childrenCount * 2200;
  const otherDepsAmount = otherDepsCount * 500;
  const otherCreditsAmount = parseFloat(federalW4Data.otherCredits) || 0;
  const step3Total = childrenAmount + otherDepsAmount + otherCreditsAmount;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={() => setCurrentView('dashboard')}
          className="flex items-center mb-6 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <div className="flex items-center mb-2">
          <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-4" />
          <h1 className="text-3xl font-bold text-gray-900">Tax Withholding Forms</h1>
        </div>
        <p className="text-gray-500 mb-8">Complete both your Federal W-4 and South Carolina W-4 forms below.</p>

        {/* SECTION 1: FEDERAL W-4 (2026) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Federal W-4 (2026)</h2>
              <p className="text-sm text-gray-500">Employee's Withholding Certificate — Internal Revenue Service</p>
            </div>
          </div>

          {/* Step 1: Personal Information */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Step 1: Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
              <div className="md:col-span-5">
                <label className={labelClass}>First Name</label>
                <input type="text" value={federalW4Data.firstName} onChange={(e) => handleFederalPersonalChange('firstName', e.target.value)} className={inputClass} placeholder="First name" />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>M.I.</label>
                <input type="text" maxLength={1} value={federalW4Data.middleInitial} onChange={(e) => handleFederalPersonalChange('middleInitial', e.target.value)} className={inputClass} placeholder="M" />
              </div>
              <div className="md:col-span-5">
                <label className={labelClass}>Last Name</label>
                <input type="text" value={federalW4Data.lastName} onChange={(e) => handleFederalPersonalChange('lastName', e.target.value)} className={inputClass} placeholder="Last name" />
              </div>
            </div>
            <div className="mb-4">
              <label className={labelClass}>Address</label>
              <input type="text" value={federalW4Data.address} onChange={(e) => handleFederalPersonalChange('address', e.target.value)} className={inputClass} placeholder="Street address" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
              <div className="md:col-span-3">
                <label className={labelClass}>City</label>
                <input type="text" value={federalW4Data.city} onChange={(e) => handleFederalPersonalChange('city', e.target.value)} className={inputClass} placeholder="City" />
              </div>
              <div className="md:col-span-1">
                <label className={labelClass}>State</label>
                <input type="text" maxLength={2} value={federalW4Data.state} onChange={(e) => handleFederalPersonalChange('state', e.target.value.toUpperCase())} className={inputClass} placeholder="SC" />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>ZIP Code</label>
                <input type="text" maxLength={10} value={federalW4Data.zipCode} onChange={(e) => handleFederalPersonalChange('zipCode', e.target.value)} className={inputClass} placeholder="ZIP code" />
              </div>
            </div>
            <div className="mb-6">
              <label className={labelClass}>Social Security Number</label>
              <input type="text" value={federalW4Data.ssn} onChange={(e) => handleFederalPersonalChange('ssn', e.target.value)} className={`${inputClass} max-w-xs`} placeholder="XXX-XX-XXXX" />
            </div>

            <label className={labelClass}>Filing Status</label>
            <div className="space-y-2">
              <label className={radioLabelClass}>
                <input type="radio" name="fedFilingStatus" value="single_separate" checked={federalW4Data.filingStatus === 'single_separate'} onChange={(e) => setFederalW4Data({...federalW4Data, filingStatus: 'single_separate'})} className="w-4 h-4 text-blue-600" />
                <span className="text-sm">Single or Married filing separately</span>
              </label>
              <label className={radioLabelClass}>
                <input type="radio" name="fedFilingStatus" value="married_jointly" checked={federalW4Data.filingStatus === 'married_jointly'} onChange={(e) => setFederalW4Data({...federalW4Data, filingStatus: 'married_jointly'})} className="w-4 h-4 text-blue-600" />
                <span className="text-sm">Married filing jointly or Qualifying surviving spouse</span>
              </label>
              <label className={radioLabelClass}>
                <input type="radio" name="fedFilingStatus" value="head_of_household" checked={federalW4Data.filingStatus === 'head_of_household'} onChange={(e) => setFederalW4Data({...federalW4Data, filingStatus: 'head_of_household'})} className="w-4 h-4 text-blue-600" />
                <span className="text-sm">Head of household</span>
              </label>
            </div>
          </div>

          {/* Step 2: Multiple Jobs */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Step 2: Multiple Jobs or Spouse Works</h3>
            <p className="text-sm text-gray-500 mb-4">Complete this step if you (1) hold more than one job at a time, or (2) are married filing jointly and your spouse also works. Otherwise, skip to Step 3.</p>
            <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
              <input type="checkbox" checked={federalW4Data.multipleJobsCheckbox} onChange={(e) => setFederalW4Data({...federalW4Data, multipleJobsCheckbox: e.target.checked})} className="w-4 h-4 text-blue-600 rounded" />
              <span className="text-sm">(c) If there are only two jobs total, you may check this box. Do the same on the W-4 for the other job.</span>
            </label>
          </div>

          {/* Step 3: Claim Dependents */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Step 3: Claim Dependents</h3>
            <p className="text-sm text-gray-500 mb-4">If your total income will be $200,000 or less ($400,000 or less if married filing jointly).</p>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className={labelClass}>(a) Qualifying children under age 17</label>
                  <p className="text-xs text-gray-500">Number of children × $2,200</p>
                </div>
                <div className="flex items-center space-x-3">
                  <input type="number" min="0" value={federalW4Data.qualifyingChildren} onChange={(e) => setFederalW4Data({...federalW4Data, qualifyingChildren: e.target.value})} className="w-20 p-2 border border-gray-300 rounded-lg text-center text-sm" placeholder="0" />
                  <span className="text-sm font-medium text-gray-700 w-24 text-right">${childrenAmount.toLocaleString()}</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className={labelClass}>(b) Other dependents</label>
                  <p className="text-xs text-gray-500">Number of other dependents × $500</p>
                </div>
                <div className="flex items-center space-x-3">
                  <input type="number" min="0" value={federalW4Data.otherDependents} onChange={(e) => setFederalW4Data({...federalW4Data, otherDependents: e.target.value})} className="w-20 p-2 border border-gray-300 rounded-lg text-center text-sm" placeholder="0" />
                  <span className="text-sm font-medium text-gray-700 w-24 text-right">${otherDepsAmount.toLocaleString()}</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className={labelClass}>Other credits (e.g., education credits)</label>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">$</span>
                  <input type="text" value={federalW4Data.otherCredits} onChange={(e) => setFederalW4Data({...federalW4Data, otherCredits: e.target.value})} className="w-28 p-2 border border-gray-300 rounded-lg text-right text-sm" placeholder="0.00" />
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-sm font-semibold text-blue-800">Line 3 Total</span>
                <span className="text-lg font-bold text-blue-800">${step3Total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Step 4: Other Adjustments */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Step 4: Other Adjustments (Optional)</h3>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>(a) Other income (not from jobs)</label>
                <p className="text-xs text-gray-500 mb-1">Interest, dividends, retirement income, etc.</p>
                <div className="flex items-center space-x-2 max-w-xs">
                  <span className="text-sm text-gray-500">$</span>
                  <input type="text" value={federalW4Data.otherIncome} onChange={(e) => setFederalW4Data({...federalW4Data, otherIncome: e.target.value})} className={inputClass} placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className={labelClass}>(b) Deductions</label>
                <p className="text-xs text-gray-500 mb-1">If you expect to claim deductions other than the standard deduction (see worksheet on page 4)</p>
                <div className="flex items-center space-x-2 max-w-xs">
                  <span className="text-sm text-gray-500">$</span>
                  <input type="text" value={federalW4Data.deductions} onChange={(e) => setFederalW4Data({...federalW4Data, deductions: e.target.value})} className={inputClass} placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className={labelClass}>(c) Extra withholding</label>
                <p className="text-xs text-gray-500 mb-1">Additional tax you want withheld each pay period</p>
                <div className="flex items-center space-x-2 max-w-xs">
                  <span className="text-sm text-gray-500">$</span>
                  <input type="text" value={federalW4Data.extraWithholding} onChange={(e) => setFederalW4Data({...federalW4Data, extraWithholding: e.target.value})} className={inputClass} placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>

          {/* Exempt */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Exemption from Withholding</h3>
            <label className="flex items-start space-x-3 p-4 border border-yellow-200 bg-yellow-50 rounded-lg cursor-pointer">
              <input type="checkbox" checked={federalW4Data.claimExempt} onChange={(e) => setFederalW4Data({...federalW4Data, claimExempt: e.target.checked})} className="w-4 h-4 text-yellow-600 rounded mt-0.5" />
              <div>
                <span className="text-sm font-medium text-yellow-800">I claim exemption from withholding for 2026</span>
                <p className="text-xs text-yellow-700 mt-1">You must meet both conditions: (1) you had no federal income tax liability last year, and (2) you expect none this year.</p>
              </div>
            </label>
          </div>

          {/* Step 5: Signature */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Step 5: Sign Here</h3>
            <p className="text-sm text-gray-500 mb-4">Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>Employee Signature (type full name)</label>
                <input type="text" value={federalW4Data.signature} onChange={(e) => setFederalW4Data({...federalW4Data, signature: e.target.value})} className={`${inputClass} italic`} placeholder="Type your full legal name" />
              </div>
              <div>
                <label className={labelClass}>Date</label>
                <input type="date" value={federalW4Data.signatureDate} onChange={(e) => setFederalW4Data({...federalW4Data, signatureDate: e.target.value})} className={inputClass} />
              </div>
            </div>
          </div>

          {/* Employer Section (read-only/HR-filled) */}
          <div className="mb-8 p-4 bg-gray-100 rounded-lg border border-gray-300">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Employers Only — To be completed by HR</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Employer name and address</label>
                <div className="p-2 bg-gray-200 rounded text-sm text-gray-500 italic">Completed by HR</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">First date of employment</label>
                <div className="p-2 bg-gray-200 rounded text-sm text-gray-500 italic">Completed by HR</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Employer Identification Number (EIN)</label>
                <div className="p-2 bg-gray-200 rounded text-sm text-gray-500 italic">Completed by HR</div>
              </div>
            </div>
          </div>

          {/* Federal W-4 Instructions & Worksheets — Reference Only */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Federal W-4 — Reference Document</h3>
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                REFERENCE ONLY
              </span>
            </div>
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                This document is provided for <strong>reference only</strong>. Do not fill out or print this form — please use the interactive fields above to complete your federal tax withholding information.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2" style={{ height: '600px' }}>
                <iframe
                  src={FEDERAL_W4_PDF_URL}
                  title="Federal W-4 (2026) — Reference Only"
                  className="w-full h-full rounded border border-gray-300 bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: SOUTH CAROLINA W-4 (2026) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">South Carolina W-4 (2026)</h2>
              <p className="text-sm text-gray-500">Employee's Withholding Allowance Certificate — SC Department of Revenue</p>
            </div>
          </div>

          <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">Personal information has been pre-populated from your Federal W-4 entries above. You may edit any field if your SC information differs.</p>
          </div>

          {/* Part I: Employee Information */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Part I — Employee Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
              <div className="md:col-span-5">
                <label className={labelClass}>First Name</label>
                <input type="text" value={scW4Data.firstName} onChange={(e) => setScW4Data({...scW4Data, firstName: e.target.value})} className={inputClass} placeholder="First name" />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>M.I.</label>
                <input type="text" maxLength={1} value={scW4Data.middleInitial} onChange={(e) => setScW4Data({...scW4Data, middleInitial: e.target.value})} className={inputClass} placeholder="M" />
              </div>
              <div className="md:col-span-5">
                <label className={labelClass}>Last Name</label>
                <input type="text" value={scW4Data.lastName} onChange={(e) => setScW4Data({...scW4Data, lastName: e.target.value})} className={inputClass} placeholder="Last name" />
              </div>
            </div>
            <div className="mb-4">
              <label className={labelClass}>Address</label>
              <input type="text" value={scW4Data.address} onChange={(e) => setScW4Data({...scW4Data, address: e.target.value})} className={inputClass} placeholder="Street address" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
              <div className="md:col-span-3">
                <label className={labelClass}>City</label>
                <input type="text" value={scW4Data.city} onChange={(e) => setScW4Data({...scW4Data, city: e.target.value})} className={inputClass} placeholder="City" />
              </div>
              <div className="md:col-span-1">
                <label className={labelClass}>State</label>
                <input type="text" maxLength={2} value={scW4Data.state} onChange={(e) => setScW4Data({...scW4Data, state: e.target.value.toUpperCase()})} className={inputClass} placeholder="SC" />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>ZIP Code</label>
                <input type="text" maxLength={10} value={scW4Data.zipCode} onChange={(e) => setScW4Data({...scW4Data, zipCode: e.target.value})} className={inputClass} placeholder="ZIP code" />
              </div>
            </div>
            <div className="mb-6">
              <label className={labelClass}>Social Security Number</label>
              <input type="text" value={scW4Data.ssn} onChange={(e) => setScW4Data({...scW4Data, ssn: e.target.value})} className={`${inputClass} max-w-xs`} placeholder="XXX-XX-XXXX" />
            </div>

            <label className={labelClass}>Filing Status (Line 3)</label>
            <div className="space-y-2 mb-6">
              <label className={radioLabelClass}>
                <input type="radio" name="scFilingStatus" value="single" checked={scW4Data.filingStatus === 'single'} onChange={() => setScW4Data({...scW4Data, filingStatus: 'single'})} className="w-4 h-4 text-green-600" />
                <span className="text-sm">Single</span>
              </label>
              <label className={radioLabelClass}>
                <input type="radio" name="scFilingStatus" value="married" checked={scW4Data.filingStatus === 'married'} onChange={() => setScW4Data({...scW4Data, filingStatus: 'married'})} className="w-4 h-4 text-green-600" />
                <span className="text-sm">Married</span>
              </label>
              <label className={radioLabelClass}>
                <input type="radio" name="scFilingStatus" value="married_higher_single" checked={scW4Data.filingStatus === 'married_higher_single'} onChange={() => setScW4Data({...scW4Data, filingStatus: 'married_higher_single'})} className="w-4 h-4 text-green-600" />
                <span className="text-sm">Married, but withhold at higher Single rate</span>
              </label>
            </div>

            <div className="mb-6">
              <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-green-50 transition-colors">
                <input type="checkbox" checked={scW4Data.lastNameDiffers} onChange={(e) => setScW4Data({...scW4Data, lastNameDiffers: e.target.checked})} className="w-4 h-4 text-green-600 rounded" />
                <span className="text-sm">Line 4: Check here if your last name differs from that shown on your social security card</span>
              </label>
            </div>

            <div className="mb-4">
              <label className={labelClass}>Line 5: Total number of allowances (from Personal Allowances Worksheet)</label>
              <input type="number" min="0" value={scW4Data.totalAllowances} onChange={(e) => setScW4Data({...scW4Data, totalAllowances: e.target.value})} className={`${inputClass} max-w-xs`} placeholder="0" />
            </div>

            <div className="mb-6">
              <label className={labelClass}>Line 6: Additional amount to be withheld from each paycheck</label>
              <div className="flex items-center space-x-2 max-w-xs">
                <span className="text-sm text-gray-500">$</span>
                <input type="text" value={scW4Data.additionalWithholding} onChange={(e) => setScW4Data({...scW4Data, additionalWithholding: e.target.value})} className={inputClass} placeholder="0.00" />
              </div>
            </div>

            <div className="mb-6">
              <h3 className={sectionHeaderClass}>Line 7: Exemption from Withholding</h3>
              <label className="flex items-start space-x-3 p-4 border border-yellow-200 bg-yellow-50 rounded-lg cursor-pointer mb-4">
                <input type="checkbox" checked={scW4Data.claimExempt} onChange={(e) => setScW4Data({...scW4Data, claimExempt: e.target.checked, exemptReason: e.target.checked ? scW4Data.exemptReason : '', militaryDomicileState: e.target.checked ? scW4Data.militaryDomicileState : ''})} className="w-4 h-4 text-yellow-600 rounded mt-0.5" />
                <div>
                  <span className="text-sm font-medium text-yellow-800">I claim exemption from South Carolina withholding</span>
                </div>
              </label>

              {scW4Data.claimExempt && (
                <div className="ml-8 space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason for exemption:</label>
                  <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-yellow-50 transition-colors">
                    <input type="radio" name="scExemptReason" value="no_liability" checked={scW4Data.exemptReason === 'no_liability'} onChange={() => setScW4Data({...scW4Data, exemptReason: 'no_liability'})} className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm">I had no SC income tax liability last year AND expect none this year</span>
                  </label>
                  <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-yellow-50 transition-colors">
                    <input type="radio" name="scExemptReason" value="military" checked={scW4Data.exemptReason === 'military'} onChange={() => setScW4Data({...scW4Data, exemptReason: 'military'})} className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm">I am a qualifying spouse of a military servicemember</span>
                  </label>
                  {scW4Data.exemptReason === 'military' && (
                    <div className="ml-8 mt-2">
                      <label className={labelClass}>State of domicile of servicemember</label>
                      <input type="text" maxLength={2} value={scW4Data.militaryDomicileState} onChange={(e) => setScW4Data({...scW4Data, militaryDomicileState: e.target.value.toUpperCase()})} className={`${inputClass} max-w-xs`} placeholder="State abbreviation" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* SC Signature */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Employee Signature</h3>
            <p className="text-sm text-gray-500 mb-4">Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>Signature (type full name)</label>
                <input type="text" value={scW4Data.signature} onChange={(e) => setScW4Data({...scW4Data, signature: e.target.value})} className={`${inputClass} italic`} placeholder="Type your full legal name" />
              </div>
              <div>
                <label className={labelClass}>Date</label>
                <input type="date" value={scW4Data.signatureDate} onChange={(e) => setScW4Data({...scW4Data, signatureDate: e.target.value})} className={inputClass} />
              </div>
            </div>
          </div>

          {/* Part II: Employer Section (HR-filled) */}
          <div className="mb-8 p-4 bg-gray-100 rounded-lg border border-gray-300">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Part II — Employer Information — To be completed by HR</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Employer name and address</label>
                <div className="p-2 bg-gray-200 rounded text-sm text-gray-500 italic">Completed by HR</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">SC Withholding File Number</label>
                <div className="p-2 bg-gray-200 rounded text-sm text-gray-500 italic">Completed by HR</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Federal Identification Number</label>
                <div className="p-2 bg-gray-200 rounded text-sm text-gray-500 italic">Completed by HR</div>
              </div>
            </div>
          </div>

          {/* SC W-4 Instructions & Worksheets — Reference Only */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">SC W-4 — Reference Document</h3>
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                REFERENCE ONLY
              </span>
            </div>
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                This document is provided for <strong>reference only</strong>. Do not fill out or print this form — please use the interactive fields above to complete your South Carolina tax withholding information.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2" style={{ height: '600px' }}>
                <iframe
                  src={SC_W4_PDF_URL}
                  title="SC W-4 (2026) — Reference Only"
                  className="w-full h-full rounded border border-gray-300 bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
	<div className="flex justify-between items-center bg-white rounded-xl shadow-sm border border-gray-200 p-6">
  	 <button
    	   onClick={() => setCurrentView('dashboard')}
           className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
  	>
    	   <ArrowLeft className="w-4 h-4 mr-2" />
    	   Back to Dashboard
  	</button>
  	<button
    	  onClick={handleTaxFormsSubmit}
    	  disabled={isSaving}
    	  className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
  	>
    	  <Send className="w-4 h-4 mr-2" />
    	  {isSaving ? 'Submitting...' : 'Submit Tax Forms'}
  	</button>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// EMPLOYEE BENEFITS REVIEW (replaces old BenefitsForm)
// ====================================================================

interface EmployeeBenefitsReviewProps {
  benefitsData: BenefitsData;
  setBenefitsData: (data: BenefitsData) => void;
  setCurrentView: (view: string) => void;
  fetchUserProfile: (token: string) => Promise<void>;
}

const EmployeeBenefitsReview: React.FC<EmployeeBenefitsReviewProps> = ({
  benefitsData,
  setBenefitsData,
  setCurrentView,
  fetchUserProfile
}) => {
  const canSubmit = benefitsData.benefitGuideAcknowledged && benefitsData.scrsHandbookAcknowledged;

const [isSaving, setIsSaving] = useState(false);
const [saveError, setSaveError] = useState('');
const [saveSuccess, setSaveSuccess] = useState(false);

const handleBenefitsSubmit = async () => {
  setIsSaving(true);
  setSaveError('');
  
  try {
    const response = await fetch('/api/benefits/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      },
      body: JSON.stringify({
        benefitsData
      })
    });

    const data = await response.json();
    
    if (data.success) {
      setSaveSuccess(true);
      // Refresh user data to update task status
      const token = localStorage.getItem('accessToken');
      if (token) {
        await fetchUserProfile(token);
      }
      // Show success message briefly, then go back
      setTimeout(() => {
        setCurrentView('dashboard');
      }, 2000);
    } else {
      setSaveError(data.message || 'Failed to save benefits acknowledgment');
    }
  } catch (error) {
    console.error('Benefits acknowledgment save error:', error);
    setSaveError('Unable to connect to server. Please try again.');
  } finally {
    setIsSaving(false);
  }
};

  const documents = [
    {
      id: 'benefitGuide',
      title: '2026 Benefit Guide — Greenwood CPW',
      url: '/documents/2026_Benefit_Guide__Greenwood_CPW.pdf',
      acknowledged: benefitsData.benefitGuideAcknowledged,
      onToggle: () => setBenefitsData({ ...benefitsData, benefitGuideAcknowledged: !benefitsData.benefitGuideAcknowledged })
    },
    {
      id: 'scrsHandbook',
      title: '2026 SCRS Retirement Handbook',
      url: '/documents/scrs_handbook_2026.pdf',
      acknowledged: benefitsData.scrsHandbookAcknowledged,
      onToggle: () => setBenefitsData({ ...benefitsData, scrsHandbookAcknowledged: !benefitsData.scrsHandbookAcknowledged })
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <button
          onClick={() => setCurrentView('dashboard')}
          className="flex items-center mb-6 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <div className="flex items-center mb-2">
            <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-4" />
            <h1 className="text-3xl font-bold text-gray-900">Employee Benefits Review</h1>
          </div>
          <p className="text-gray-500">
            Please review each of the following benefits documents in full, then check the acknowledgment box below each one.
          </p>
        </div>

        <div className="space-y-8">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${doc.acknowledged ? 'bg-green-100' : 'bg-blue-100'}`}>
                    {doc.acknowledged
                      ? <CheckCircle className="w-5 h-5 text-green-600" />
                      : <FileText className="w-5 h-5 text-blue-600" />
                    }
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{doc.title}</h2>
                    <p className="text-sm text-gray-500">Scroll through the full document below</p>
                  </div>
                </div>
                {doc.acknowledged && (
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-green-100 text-green-800">
                    ACKNOWLEDGED
                  </span>
                )}
              </div>

              <div className="bg-gray-100 p-2" style={{ height: '600px' }}>
                <iframe
                  src={doc.url}
                  title={doc.title}
                  className="w-full h-full rounded border border-gray-300 bg-white"
                />
              </div>

              <div className="p-6 border-t border-gray-200">
                <label className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-colors ${doc.acknowledged ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200 hover:bg-blue-50'}`}>
                  <input
                    type="checkbox"
                    checked={doc.acknowledged}
                    onChange={doc.onToggle}
                    className="w-5 h-5 text-green-600 rounded mt-0.5"
                  />
                  <span className="text-sm text-gray-800">
                    I have read and reviewed the <strong>{doc.title}</strong> in its entirety and understand the information presented.
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>

        {!canSubmit && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Please review and acknowledge both documents before submitting.
            </p>
          </div>
        )}

        <div className="flex justify-between items-center mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
	  <button
  	    onClick={handleBenefitsSubmit}
  	    disabled={!canSubmit || isSaving}
  	    className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
	  >
  	    <Send className="w-4 h-4 mr-2" />
  	    {isSaving ? 'Submitting...' : 'Submit Acknowledgment'}
	 </button>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// EMPLOYEE HANDBOOK REVIEW COMPONENT
// ====================================================================

interface EmployeeHandbookReviewProps {
  handbookData: HandbookData;
  setHandbookData: (data: HandbookData) => void;
  setCurrentView: (view: string) => void;
  fetchUserProfile: (token: string) => Promise<void>;
}

const EmployeeHandbookReview: React.FC<EmployeeHandbookReviewProps> = ({
  handbookData,
  setHandbookData,
  setCurrentView,
  fetchUserProfile
}) => {
  const canSubmit = handbookData.handbookAcknowledged;

const [isSaving, setIsSaving] = useState(false);
const [saveError, setSaveError] = useState('');
const [saveSuccess, setSaveSuccess] = useState(false);

const handleHandbookSubmit = async () => {
  setIsSaving(true);
  setSaveError('');
  
  try {
    const response = await fetch('/api/handbook/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      },
      body: JSON.stringify({
        handbookData
      })
    });

    const data = await response.json();
    
    if (data.success) {
      setSaveSuccess(true);
      // Refresh user data to update task status
      const token = localStorage.getItem('accessToken');
      if (token) {
        await fetchUserProfile(token);
      }
      // Show success message briefly, then go back
      setTimeout(() => {
        setCurrentView('dashboard');
      }, 2000);
    } else {
      setSaveError(data.message || 'Failed to save handbook acknowledgment');
    }
  } catch (error) {
    console.error('Handbook acknowledgment save error:', error);
    setSaveError('Unable to connect to server. Please try again.');
  } finally {
    setIsSaving(false);
  }
};

  const document = {
    id: 'personnelHandbook',
    title: 'Greenwood CPW Personnel Handbook',
    url: '/documents/Personnel_Handbook.pdf',
    acknowledged: handbookData.handbookAcknowledged,
    onToggle: () => setHandbookData({ ...handbookData, handbookAcknowledged: !handbookData.handbookAcknowledged })
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <button
          onClick={() => setCurrentView('dashboard')}
          className="flex items-center mb-6 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <div className="flex items-center mb-2">
            <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-4" />
            <h1 className="text-3xl font-bold text-gray-900">Employee Handbook Review</h1>
          </div>
          <p className="text-gray-500">
            Please review the employee handbook in full, then check the acknowledgment box below.
          </p>
        </div>

        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${document.acknowledged ? 'bg-green-100' : 'bg-blue-100'}`}>
                  {document.acknowledged
                    ? <CheckCircle className="w-5 h-5 text-green-600" />
                    : <FileText className="w-5 h-5 text-blue-600" />
                  }
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{document.title}</h2>
                  <p className="text-sm text-gray-500">Scroll through the full document below</p>
                </div>
              </div>
              {document.acknowledged && (
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-green-100 text-green-800">
                  ACKNOWLEDGED
                </span>
              )}
            </div>

            <div className="bg-gray-100 p-2" style={{ height: '600px' }}>
              <iframe
                src={document.url}
                title={document.title}
                className="w-full h-full rounded border border-gray-300 bg-white"
              />
            </div>

            <div className="p-6 border-t border-gray-200">
              <label className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-colors ${document.acknowledged ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200 hover:bg-blue-50'}`}>
                <input
                  type="checkbox"
                  checked={document.acknowledged}
                  onChange={document.onToggle}
                  className="w-5 h-5 text-green-600 rounded mt-0.5"
                />
                <span className="text-sm text-gray-800">
                  I have read and reviewed the <strong>{document.title}</strong> in its entirety and understand the information presented.
                </span>
              </label>
            </div>
          </div>
        </div>

        {!canSubmit && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Please review and acknowledge the handbook before submitting.
            </p>
          </div>
        )}

        <div className="flex justify-between items-center mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
	  <button
  	    onClick={handleHandbookSubmit}
  	    disabled={!canSubmit || isSaving}
  	    className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
	  >
  	    <Send className="w-4 h-4 mr-2" />
  	    {isSaving ? 'Submitting...' : 'Submit Acknowledgment'}
	  </button>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// DIRECT DEPOSIT FORM
// ====================================================================

interface DirectDepositFormProps {
  directDepositData: DirectDepositData;
  setDirectDepositData: (data: DirectDepositData) => void;
  directDepositFiles: DirectDepositFiles;
  setDirectDepositFiles: (files: DirectDepositFiles) => void;
  setCurrentView: (view: string) => void;
  fetchUserProfile: (token: string) => Promise<void>;
}

const DirectDepositForm: React.FC<DirectDepositFormProps> = ({
  directDepositData,
  setDirectDepositData,
  directDepositFiles,
  setDirectDepositFiles,
  setCurrentView,
  fetchUserProfile
}) => {
  const inputClass = "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const sectionHeaderClass = "text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200";
  const radioLabelClass = "flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors";

  const handleVoidedCheckUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    const file = event.target.files[0];
    const newFile: UploadedFile = {
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toLocaleDateString()
    };
    setDirectDepositFiles({ voidedCheck: newFile });
  };

const [isSaving, setIsSaving] = useState(false);
const [saveError, setSaveError] = useState('');
const [saveSuccess, setSaveSuccess] = useState(false);

const handleDirectDepositSubmit = async () => {
  setIsSaving(true);
  setSaveError('');
  
  try {
    const response = await fetch('/api/direct-deposit/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      },
      body: JSON.stringify({
        directDepositData,
        directDepositFiles
      })
    });

    const data = await response.json();
    
    if (data.success) {
      setSaveSuccess(true);
      // Refresh user data to update task status
      const token = localStorage.getItem('accessToken');
      if (token) {
        await fetchUserProfile(token);
      }
      // Show success message briefly, then go back
      setTimeout(() => {
        setCurrentView('dashboard');
      }, 2000);
    } else {
      setSaveError(data.message || 'Failed to save direct deposit information');
    }
  } catch (error) {
    console.error('Direct deposit save error:', error);
    setSaveError('Unable to connect to server. Please try again.');
  } finally {
    setIsSaving(false);
  }
};

  const canSubmit =
    directDepositData.depositoryName.trim() !== '' &&
    directDepositData.depositoryCity.trim() !== '' &&
    directDepositData.depositoryState.trim() !== '' &&
    directDepositData.transitABANumber.trim() !== '' &&
    directDepositData.accountNumber.trim() !== '' &&
    directDepositData.accountType !== '' &&
    directDepositData.depositType !== '' &&
    (directDepositData.depositType === 'full' || (directDepositData.depositType === 'specific' && directDepositData.specificAmount.trim() !== '')) &&
    directDepositData.authorizationAcknowledged &&
    directDepositData.signature.trim() !== '' &&
    directDepositData.signatureDate.trim() !== '';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={() => setCurrentView('dashboard')}
          className="flex items-center mb-6 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <div className="flex items-center mb-2">
          <img src={CPW_LOGO_URL} alt="Greenwood CPW" className="h-10 mr-4" />
          <h1 className="text-3xl font-bold text-gray-900">Direct Deposit Authorization</h1>
        </div>
        <p className="text-gray-500 mb-8">Complete the form below to authorize payroll direct deposit to your bank account.</p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <Landmark className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Payroll Direct Deposit Authorization Form</h2>
              <p className="text-sm text-gray-500">Greenwood Commissioners of Public Works (CPW)</p>
            </div>
          </div>

          {/* Authorization Disclaimer */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Authorization Agreement</h3>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                I understand that by completing this form, I am authorizing the Greenwood Commissioners of Public Works (CPW) to make direct deposit of my pay as directed below to the account and financial institution indicated below. I also understand that this authority is to remain in full force until CPW has received written notification from me of its termination in such time and in such manner as to afford CPW and the depository a reasonable opportunity to act on it. I also understand that the total amount of direct deposit cannot exceed my gross pay less all required taxes, retirement contributions, and other authorized payroll deductions. Therefore, I hereby authorize CPW to initiate credit entries and to initiate, if necessary, debit entries and adjustments for any credit entries in error to my account indicated below and the depository named below to credit and/or debit the same to such account.
              </p>
            </div>
            <label className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-colors ${directDepositData.authorizationAcknowledged ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200 hover:bg-blue-50'}`}>
              <input
                type="checkbox"
                checked={directDepositData.authorizationAcknowledged}
                onChange={(e) => setDirectDepositData({...directDepositData, authorizationAcknowledged: e.target.checked})}
                className="w-5 h-5 text-green-600 rounded mt-0.5"
              />
              <span className="text-sm text-gray-800">
                I have read and understand the above authorization agreement. <span className="text-red-500">*</span>
              </span>
            </label>
          </div>

          {/* Depository Information */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Depository Information</h3>
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                The depository information below must be provided by your financial institution, or you may attach a voided check/deposit slip using the upload section below.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>Depository Name (Bank/Credit Union) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={directDepositData.depositoryName}
                  onChange={(e) => setDirectDepositData({...directDepositData, depositoryName: e.target.value})}
                  className={inputClass}
                  placeholder="e.g., First National Bank"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-4">
                  <label className={labelClass}>City <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={directDepositData.depositoryCity}
                    onChange={(e) => setDirectDepositData({...directDepositData, depositoryCity: e.target.value})}
                    className={inputClass}
                    placeholder="City"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>State <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    maxLength={2}
                    value={directDepositData.depositoryState}
                    onChange={(e) => setDirectDepositData({...directDepositData, depositoryState: e.target.value.toUpperCase()})}
                    className={inputClass}
                    placeholder="SC"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Transit/ABA Routing Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={directDepositData.transitABANumber}
                  onChange={(e) => setDirectDepositData({...directDepositData, transitABANumber: e.target.value})}
                  className={`${inputClass} max-w-sm`}
                  placeholder="9-digit routing number"
                  maxLength={9}
                />
                <p className="text-xs text-gray-500 mt-1">The 9-digit number found at the bottom-left of your checks</p>
              </div>

              <div>
                <label className={labelClass}>Account Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={directDepositData.accountNumber}
                  onChange={(e) => setDirectDepositData({...directDepositData, accountNumber: e.target.value})}
                  className={`${inputClass} max-w-sm`}
                  placeholder="Account number"
                />
              </div>

              <div>
                <label className={labelClass}>Account Type <span className="text-red-500">*</span></label>
                <div className="flex gap-4">
                  <label className={`${radioLabelClass} flex-1`}>
                    <input
                      type="radio"
                      name="accountType"
                      value="checking"
                      checked={directDepositData.accountType === 'checking'}
                      onChange={() => setDirectDepositData({...directDepositData, accountType: 'checking'})}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Checking</span>
                  </label>
                  <label className={`${radioLabelClass} flex-1`}>
                    <input
                      type="radio"
                      name="accountType"
                      value="savings"
                      checked={directDepositData.accountType === 'savings'}
                      onChange={() => setDirectDepositData({...directDepositData, accountType: 'savings'})}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Savings</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Deposit Amount */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Amount to be Deposited</h3>
            <div className="space-y-3">
              <label className={`${radioLabelClass} ${directDepositData.depositType === 'full' ? 'bg-blue-50 border-blue-300' : ''}`}>
                <input
                  type="radio"
                  name="depositType"
                  value="full"
                  checked={directDepositData.depositType === 'full'}
                  onChange={() => setDirectDepositData({...directDepositData, depositType: 'full', specificAmount: ''})}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <span className="text-sm font-medium">Full amount</span>
                  <p className="text-xs text-gray-500">Full amount of gross pay less all applicable taxes, retirement contributions, and deductions</p>
                </div>
              </label>
              <label className={`${radioLabelClass} ${directDepositData.depositType === 'specific' ? 'bg-blue-50 border-blue-300' : ''}`}>
                <input
                  type="radio"
                  name="depositType"
                  value="specific"
                  checked={directDepositData.depositType === 'specific'}
                  onChange={() => setDirectDepositData({...directDepositData, depositType: 'specific'})}
                  className="w-4 h-4 text-blue-600"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium">Specific dollar amount</span>
                  {directDepositData.depositType === 'specific' && (
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-sm text-gray-500">$</span>
                      <input
                        type="text"
                        value={directDepositData.specificAmount}
                        onChange={(e) => setDirectDepositData({...directDepositData, specificAmount: e.target.value})}
                        className="w-40 p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Voided Check Upload (Optional) */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Voided Check / Deposit Slip <span className="text-xs font-normal text-gray-400 ml-2">(Optional)</span></h3>
            <p className="text-sm text-gray-500 mb-4">You may attach a voided check or deposit slip for the respective account to help verify your banking information.</p>

            <div className={`border-2 rounded-lg p-6 transition-colors ${directDepositFiles.voidedCheck ? 'border-green-300 bg-green-50' : 'border-dashed border-gray-300 bg-white'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Voided Check or Deposit Slip</h3>
                  <p className="text-sm text-gray-500 mt-1">Upload a photo or scan of a voided check or deposit slip</p>
                </div>
                {directDepositFiles.voidedCheck ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <Upload className="w-6 h-6 text-gray-400 flex-shrink-0" />
                )}
              </div>

              {directDepositFiles.voidedCheck ? (
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                  <div className="flex items-center min-w-0">
                    <FileCheck className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{directDepositFiles.voidedCheck.name}</p>
                      <p className="text-xs text-gray-500">Uploaded {directDepositFiles.voidedCheck.uploadDate}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setDirectDepositFiles({ voidedCheck: null })}
                    className="ml-3 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center pt-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleVoidedCheckUpload}
                    className="hidden"
                    id="upload-voided-check"
                  />
                  <label
                    htmlFor="upload-voided-check"
                    className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </label>
                  <p className="text-xs text-gray-400 mt-2">PDF, JPG, or PNG accepted</p>
                </div>
              )}
            </div>
          </div>

          {/* Employee Information */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Employee Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Employee Name (Type or Print) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={directDepositData.employeeName}
                  onChange={(e) => setDirectDepositData({...directDepositData, employeeName: e.target.value})}
                  className={inputClass}
                  placeholder="Your full legal name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Employee Payroll ID#</label>
                <div className="p-3 bg-gray-200 rounded-lg text-sm text-gray-500 italic">Completed by HR</div>
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="mb-8">
            <h3 className={sectionHeaderClass}>Employee Signature</h3>
            <p className="text-sm text-gray-500 mb-4">By signing below, you confirm that all information provided is accurate and you authorize direct deposit as described above.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>Signature (type full name) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={directDepositData.signature}
                  onChange={(e) => setDirectDepositData({...directDepositData, signature: e.target.value})}
                  className={`${inputClass} italic`}
                  placeholder="Type your full legal name"
                />
              </div>
              <div>
                <label className={labelClass}>Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={directDepositData.signatureDate}
                  onChange={(e) => setDirectDepositData({...directDepositData, signatureDate: e.target.value})}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        {!canSubmit && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Please complete all required fields, acknowledge the authorization agreement, and sign the form before submitting.
            </p>
          </div>
        )}

        <div className="flex justify-between items-center bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
	  <button
  	    onClick={handleDirectDepositSubmit}
 	    disabled={!canSubmit || isSaving}
  	    className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
	  >
 	   <Send className="w-4 h-4 mr-2" />
  	   {isSaving ? 'Submitting...' : 'Submit Direct Deposit'}
	 </button>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// MAIN COMPONENT
// ====================================================================

const OnboardingApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [isHRAdmin, setIsHRAdmin] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [isCreatingEmployee, setIsCreatingEmployee] = useState(false);
  const [createEmployeeError, setCreateEmployeeError] = useState('');
  const [createEmployeeSuccess, setCreateEmployeeSuccess] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');

  // Purge Employee state
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [purgeError, setPurgeError] = useState('');
  const [purgeSuccess, setPurgeSuccess] = useState('');

  const [newEmployeeForm, setNewEmployeeForm] = useState<NewEmployeeForm>({
    firstName: '',
    lastName: '',
    email: '',
    position: '',
    department: '',
    startDate: '',
    temporaryPassword: ''
  });

  const [employees, setEmployees] = useState<Employee[]>([]);

  const [personalInfoData, setPersonalInfoData] = useState<PersonalInfo>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    emergencyContact: '',
    emergencyPhone: ''
  });

  const [federalW4Data, setFederalW4Data] = useState<FederalW4Data>({
    firstName: '',
    middleInitial: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    ssn: '',
    filingStatus: '',
    multipleJobsCheckbox: false,
    qualifyingChildren: '',
    otherDependents: '',
    otherCredits: '',
    otherIncome: '',
    deductions: '',
    extraWithholding: '',
    claimExempt: false,
    signature: '',
    signatureDate: ''
  });

  const [scW4Data, setScW4Data] = useState<SCW4Data>({
    firstName: '',
    middleInitial: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    ssn: '',
    filingStatus: '',
    lastNameDiffers: false,
    totalAllowances: '',
    additionalWithholding: '',
    claimExempt: false,
    exemptReason: '',
    militaryDomicileState: '',
    signature: '',
    signatureDate: ''
  });

  const [benefitsData, setBenefitsData] = useState<BenefitsData>({
    benefitGuideAcknowledged: false,
    scrsHandbookAcknowledged: false
  });

  const [handbookData, setHandbookData] = useState<HandbookData>({
    handbookAcknowledged: false
  });

  const [directDepositData, setDirectDepositData] = useState<DirectDepositData>({
    depositoryName: '',
    depositoryCity: '',
    depositoryState: '',
    transitABANumber: '',
    accountNumber: '',
    accountType: '',
    depositType: '',
    specificAmount: '',
    employeeName: '',
    payrollId: '',
    authorizationAcknowledged: false,
    signature: '',
    signatureDate: ''
  });

  const [directDepositFiles, setDirectDepositFiles] = useState<DirectDepositFiles>({
    voidedCheck: null
  });

  const [identificationFiles, setIdentificationFiles] = useState<IdentificationFiles>({
    driversLicense: null,
    socialSecurity: null,
    other: null
  });

  const [userData, setUserData] = useState({
    name: 'Sarah Johnson',
    position: 'Software Developer',
    department: 'Engineering',
    startDate: 'March 15, 2024',
    employeeId: 'EMP-2024-0156'
  });

useEffect(() => {
  if (currentView === 'hr-admin') {
    fetchEmployees();
  }
}, [currentView]);

const currentEmployee = employees.length > 0 ? employees[0] : null;

const onboardingTasks: OnboardingTask[] = currentEmployee ? [
    {
      id: 'personalInfo',
      title: 'Personal Information',
      description: 'Complete your basic personal details and contact information',
      status: currentEmployee.tasks.personalInfo.status,
      icon: User,
      progress: currentEmployee.tasks.personalInfo.status === 'completed' ? 100 : 0
    },
    {
      id: 'identification',
      title: 'Upload Identification',
      description: 'Upload driver\'s license or passport for verification',
      status: currentEmployee.tasks.identification.status,
      icon: Upload,
      progress: currentEmployee.tasks.identification.status === 'completed' ? 100 : currentEmployee.tasks.identification.status === 'in-progress' ? 50 : 0
    },
    {
      id: 'taxForms',
      title: 'Tax Forms (W-4)',
      description: 'Complete federal and state tax withholding forms',
      status: currentEmployee.tasks.taxForms.status,
      icon: FileText,
      progress: currentEmployee.tasks.taxForms.status === 'completed' ? 100 : 0
    },
    {
      id: 'directDeposit',
      title: 'Direct Deposit',
      description: 'Set up payroll direct deposit to your bank account',
      status: currentEmployee.tasks.directDeposit.status,
      icon: Landmark,
      progress: currentEmployee.tasks.directDeposit.status === 'completed' ? 100 : 0
    },
    {
      id: 'benefits',
      title: 'Employee Benefits Review',
      description: 'Review benefits documents and acknowledge receipt',
      status: currentEmployee.tasks.benefits.status,
      icon: Shield,
      progress: currentEmployee.tasks.benefits.status === 'completed' ? 100 : 0
    },
    {
      id: 'policies',
      title: 'Employee Handbook Review',
      description: 'Review and acknowledge the employee handbook',
      status: currentEmployee.tasks.policies.status,
      icon: PenTool,
      progress: currentEmployee.tasks.policies.status === 'completed' ? 100 : 0
    }
] : [];

  const handleCreateEmployee = async () => {
    setCreateEmployeeError('');
    setCreateEmployeeSuccess('');
    setIsCreatingEmployee(true);

    try {
      const response = await fetch('/api/employees/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          ...newEmployeeForm,
          sendWelcomeEmail: true,
          welcomeEmailData: {
            recipientEmail: newEmployeeForm.email,
            recipientName: `${newEmployeeForm.firstName} ${newEmployeeForm.lastName}`,
            temporaryPassword: newEmployeeForm.temporaryPassword,
            portalLink: 'https://onboarding.greenwoodcpw.com', 
            message: `Welcome to CPW! We are excited for you to join our team! Please use the link below and the temporary password to access your Onboarding Portal.`
          }
        }),
      });

      const data = await response.json();

      if (data.success) {
        const newEmployee: Employee = {
          id: Date.now(),
          name: `${newEmployeeForm.firstName} ${newEmployeeForm.lastName}`,
          email: newEmployeeForm.email,
          position: newEmployeeForm.position,
          department: newEmployeeForm.department,
          startDate: newEmployeeForm.startDate,
          progress: 0,
          tasks: {
            personalInfo: { status: 'pending', data: {} },
            identification: { status: 'pending', data: {} },
            taxForms: { status: 'pending', data: {} },
            directDeposit: { status: 'pending', data: {} },
            benefits: { status: 'pending', data: {} },
            policies: { status: 'pending', data: {} }
          }
        };
        
        setEmployees([...employees, newEmployee]);
        setCreateEmployeeSuccess(`Employee created successfully! A welcome email with the temporary password has been sent to ${newEmployeeForm.email}.`);
        
        setTimeout(() => {
          setShowAddEmployeeModal(false);
          setCreateEmployeeSuccess('');
          setNewEmployeeForm({
            firstName: '',
            lastName: '',
            email: '',
            position: '',
            department: '',
            startDate: '',
            temporaryPassword: ''
          });
        }, 3000);
      } else {
        setCreateEmployeeError(data.message || 'Failed to create employee. Please try again.');
      }
    } catch (error) {
      console.error('Create employee error:', error);
      setCreateEmployeeError('Unable to connect to server. Please try again.');
    } finally {
      setIsCreatingEmployee(false);
    }
  };

  const handlePurgeEmployee = async (employee: Employee) => {
    setPurgeError('');
    setPurgeSuccess('');
    setIsPurging(true);

    try {
      const response = await fetch(`/api/employees/${employee.id}/purge`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Remove from local state
        setEmployees(prev => prev.filter(emp => emp.id !== employee.id));
        setPurgeSuccess(`All data for ${employee.name} has been permanently deleted.`);

        setTimeout(() => {
          setShowPurgeModal(false);
          setPurgeSuccess('');
        }, 2500);
      } else {
        setPurgeError(data.message || 'Failed to purge employee. Please try again.');
      }
    } catch (error) {
      console.error('Purge employee error:', error);
      setPurgeError('Unable to connect to server. Please try again.');
    } finally {
      setIsPurging(false);
    }
  };

  const handleLogin = async () => {
    setLoginError('');
    setIsLoggingIn(true);
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setAccessToken(data.data.accessToken);
	localStorage.setItem('accessToken', data.data.accessToken);
        setIsLoggedIn(true);
        
	  setUserData({
    		name: `${data.data.user.firstName || ''} ${data.data.user.lastName || ''}`.trim(),
    		position: data.data.user.position || 'N/A',
    		department: data.data.user.department || 'N/A', 
  		startDate: data.data.user.startDate ? new Date(data.data.user.startDate || data.data.user.start_date).toLocaleDateString('en-US', { 
    			year: 'numeric', 
   			month: 'long', 
    			day: 'numeric' 
  		}) : '',  
  		employeeId: data.data.user.employeeId || ''  
	});

	await fetchUserProfile(data.data.accessToken);

	if (data.data.user.mustChangePassword) {
  	  setMustChangePassword(true);
  	  setCurrentView('change-password');
	} else if (data.data.user.role === 'HR_ADMIN') {
  	  setIsHRAdmin(true);
  	  setCurrentView('hr-admin');
	} else {
  	  setIsHRAdmin(false);
  	  setCurrentView('dashboard');
	}
      } else {
        setLoginError(data.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Unable to connect to server. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

const handleLogout = () => {
  setIsLoggedIn(false);
  setLoginData({ email: '', password: '' });
  setAccessToken('');
  setMustChangePassword(false);
};

const fetchUserProfile = async (token: string) => {
  try {
    const response = await fetch('/api/auth/me', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    const profileData = await response.json();
    
    if (profileData.success) {
      // Update employees array with real user data and tasks
      setEmployees([{
        id: profileData.data.user.id,
        name: `${profileData.data.user.firstName} ${profileData.data.user.lastName}`,
        position: profileData.data.user.position || 'N/A',
        department: profileData.data.user.department || 'N/A',
        email: profileData.data.user.email,
        startDate: profileData.data.user.startDate || '',
        progress: profileData.data.progress || 0,
        tasks: profileData.data.tasks
      }]);
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
  }
};

  const handleIdentificationUpload = (event: React.ChangeEvent<HTMLInputElement>, category: keyof IdentificationFiles) => {
    if (!event.target.files || !event.target.files[0]) return;
    
    const file = event.target.files[0];
    const newFile: UploadedFile = {
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toLocaleDateString(),
      actualFile: file
    };
    setIdentificationFiles(prev => ({ ...prev, [category]: newFile }));
  };

const handleIdentificationSubmit = async () => {
  try {
    const documents = [];

    // Helper function to convert file to base64
    const fileToBase64 = (file: File) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    };

// Process each file and convert to base64
if (identificationFiles.driversLicense?.actualFile) {
  const base64Data = await fileToBase64(identificationFiles.driversLicense.actualFile);
  documents.push({ 
    document_type: 'drivers_license', 
    file_name: identificationFiles.driversLicense.name,
    file_data: base64Data,
    file_size: identificationFiles.driversLicense.size,
    file_type: identificationFiles.driversLicense.type
  });
}

if (identificationFiles.socialSecurity?.actualFile) {
  const base64Data = await fileToBase64(identificationFiles.socialSecurity.actualFile);
  documents.push({ 
    document_type: 'social_security', 
    file_name: identificationFiles.socialSecurity.name,
    file_data: base64Data,
    file_size: identificationFiles.socialSecurity.size,
    file_type: identificationFiles.socialSecurity.type
  });
}

if (identificationFiles.other?.actualFile) {
  const base64Data = await fileToBase64(identificationFiles.other.actualFile);
  documents.push({ 
    document_type: 'other', 
    file_name: identificationFiles.other.name,
    file_data: base64Data,
    file_size: identificationFiles.other.size,
    file_type: identificationFiles.other.type
  });
}

    const response = await fetch('/api/documents/upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ documents }),
    });

    const data = await response.json();
    if (data.success) {
      await fetchUserProfile(accessToken);
      setCurrentView('dashboard');
    }
  } catch (error) {
    console.error('Document upload error:', error);
  }
};

const fetchEmployees = async () => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const response = await fetch('/api/employees', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    
    if (data.success) {
      setEmployees(data.data);
    } else {
      console.error('Failed to fetch employees:', data.message);
    }
  } catch (error) {
    console.error('Error fetching employees:', error);
  }
};

  // Main render logic — wrapped with CPW brand style injection
  const renderContent = () => {
if (!isLoggedIn) {
  if (currentView === 'forgot-password') {
    return (
      <ForgotPassword
        resetEmail={resetEmail}
        setResetEmail={setResetEmail}
        setCurrentView={setCurrentView}
      />
    );
  }

  if (currentView === 'reset-password') {
    return (
      <ResetPassword
        resetEmail={resetEmail}
        setResetEmail={setResetEmail}
        resetCode={resetCode}
        setResetCode={setResetCode}
        setCurrentView={setCurrentView}
      />
    );
  }

  return (
    <LoginScreen
      loginData={loginData}
      setLoginData={setLoginData}
      loginError={loginError}
      showPassword={showPassword}
      setShowPassword={setShowPassword}
      handleLogin={handleLogin}
      isLoggingIn={isLoggingIn}
      setCurrentView={setCurrentView}
    />
  );
}

if (mustChangePassword && currentView === 'change-password') {
  return (
    <ForceChangePassword
      accessToken={accessToken}
      onPasswordChanged={() => {
        setMustChangePassword(false);
        if (isHRAdmin) {
          setCurrentView('hr-admin');
        } else {
          setCurrentView('dashboard');
        }
      }}
      handleLogout={handleLogout}
    />
  );
}

    if (currentView === 'hr-admin') {
      return (
        <>
          <HRAdminPanel
            employees={employees}
            setShowAddEmployeeModal={setShowAddEmployeeModal}
            setShowPurgeModal={setShowPurgeModal}
            setIsLoggedIn={handleLogout}
            setSelectedEmployee={setSelectedEmployee}
            setCurrentView={setCurrentView}
          />
          {showAddEmployeeModal && (
            <AddEmployeeModal
              newEmployeeForm={newEmployeeForm}
              setNewEmployeeForm={setNewEmployeeForm}
              setShowAddEmployeeModal={setShowAddEmployeeModal}
              createEmployeeError={createEmployeeError}
              createEmployeeSuccess={createEmployeeSuccess}
              isCreatingEmployee={isCreatingEmployee}
              handleCreateEmployee={handleCreateEmployee}
              setCreateEmployeeError={setCreateEmployeeError}
              setCreateEmployeeSuccess={setCreateEmployeeSuccess}
            />
          )}
          {showPurgeModal && (
            <PurgeEmployeeModal
              employees={employees}
              setShowPurgeModal={setShowPurgeModal}
              handlePurgeEmployee={handlePurgeEmployee}
              isPurging={isPurging}
              purgeError={purgeError}
              purgeSuccess={purgeSuccess}
              setPurgeError={setPurgeError}
              setPurgeSuccess={setPurgeSuccess}
            />
          )}
        </>
      );
    }

    if (currentView === 'employee-details' && selectedEmployee) {
      return (
        <EmployeeDetailsView
          selectedEmployee={selectedEmployee}
          setCurrentView={setCurrentView}
          setIsLoggedIn={handleLogout}
          accessToken={accessToken}
        />
      );
    }

    if (currentView === 'personalInfo') {
      return (
        <PersonalInfoForm
          personalInfoData={personalInfoData}
          setPersonalInfoData={setPersonalInfoData}
          setCurrentView={setCurrentView}
	  fetchUserProfile={fetchUserProfile}
        />
      );
    }

    if (currentView === 'identification') {
      return (
        <IdentificationUpload
          identificationFiles={identificationFiles}
          handleIdentificationUpload={handleIdentificationUpload}
          handleIdentificationSubmit={handleIdentificationSubmit}
          setCurrentView={setCurrentView}
          fetchUserProfile={fetchUserProfile}
        />
      );
    }

    if (currentView === 'taxForms') {
      return (
        <W4Form
          federalW4Data={federalW4Data}
          setFederalW4Data={setFederalW4Data}
          scW4Data={scW4Data}
          setScW4Data={setScW4Data}
          setCurrentView={setCurrentView}
	  fetchUserProfile={fetchUserProfile}
        />
      );
    }

    if (currentView === 'directDeposit') {
      return (
        <DirectDepositForm
          directDepositData={directDepositData}
          setDirectDepositData={setDirectDepositData}
          directDepositFiles={directDepositFiles}
          setDirectDepositFiles={setDirectDepositFiles}
          setCurrentView={setCurrentView}
	  fetchUserProfile={fetchUserProfile}
        />
      );
    }

    if (currentView === 'benefits') {
      return (
        <EmployeeBenefitsReview
          benefitsData={benefitsData}
          setBenefitsData={setBenefitsData}
          setCurrentView={setCurrentView}
	  fetchUserProfile={fetchUserProfile}
        />
      );
    }

    if (currentView === 'policies') {
      return (
        <EmployeeHandbookReview
          handbookData={handbookData}
          setHandbookData={setHandbookData}
          setCurrentView={setCurrentView}
	  fetchUserProfile={fetchUserProfile}
        />
      );
    }

    return (
      <Dashboard
        userData={userData}
        onboardingTasks={onboardingTasks}
        setCurrentView={setCurrentView}
        setIsLoggedIn={handleLogout}
      />
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CPW_BRAND_STYLES }} />
      {renderContent()}
    </>
  );
};

export default OnboardingApp;
